// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║  ReportsPage.js  —  PARTE 1 di 2                                           ║
// ║  Incolla questa parte SOPRA la Parte 2 per ottenere il file completo.      ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

import { useEffect, useState, useMemo, useRef } from "react";
// AGGIUNGI (come nell'originale):
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
// AGGIUNGI questa al suo posto:
import { useUser } from "./UserContext";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  TrendingUp, Users, Calendar, Banknote, Stethoscope,
  FileText, Download, BarChart3, Award, Tag, Building2,
  ArrowUpRight, ArrowDownRight, CreditCard, Receipt, Wallet,
  Shield, AlertTriangle, Clock, UserCheck, UserX, BedDouble,
  CalendarDays, Activity, Package, Timer,
  Briefcase, TrendingDown, Layers, Wrench,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfYear, endOfYear,
  subMonths, subYears, subWeeks, format
} from "date-fns";
import moment from "moment";
import "moment/locale/it";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const PALETTE = [
  '#1d4ed8','#0891b2','#059669','#d97706',
  '#7c3aed','#db2777','#0f766e','#b45309','#1e40af','#065f46',
];
const METHOD_LABELS = {
  contanti: '💵 Contanti',
  carta:    '💳 Carta',
  bonifico: '🏦 Bonifico',
};

// ─── PURE HELPERS — Performance ───────────────────────────────────────────────
const fmt = (n) =>
  `€ ${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1000)
    return `€ ${((n || 0) / 1000).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
  return `€ ${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const parseDate = (d) => {
  if (!d) return null;
  if (d?.toDate) return d.toDate();
  return new Date(d);
};

const getRange = (period, cs, ce) => {
  const now = new Date();
  switch (period) {
    case 'week': {
      const d = new Date(now); const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1); d.setHours(0, 0, 0, 0);
      const end = new Date(d); end.setDate(d.getDate() + 6); end.setHours(23, 59, 59, 999);
      return { start: d, end };
    }
    case 'month':     return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'lastmonth': { const m = subMonths(now, 1); return { start: startOfMonth(m), end: endOfMonth(m) }; }
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      return { start: new Date(now.getFullYear(), qm, 1), end: new Date(now.getFullYear(), qm + 3, 0, 23, 59, 59) };
    }
    case 'year':   return { start: startOfYear(now), end: endOfYear(now) };
    case 'custom': return {
      start: cs ? new Date(cs + 'T00:00:00') : startOfMonth(now),
      end:   ce ? new Date(ce + 'T23:59:59') : endOfMonth(now),
    };
    default: return { start: startOfMonth(now), end: endOfMonth(now) };
  }
};

const getPrevRange = (period, cs, ce) => {
  const now = new Date();
  switch (period) {
    case 'week': {
      const prev = subWeeks(now, 1); const day = prev.getDay() || 7;
      prev.setDate(prev.getDate() - day + 1); prev.setHours(0, 0, 0, 0);
      const end = new Date(prev); end.setDate(prev.getDate() + 6); end.setHours(23, 59, 59, 999);
      return { start: prev, end };
    }
    case 'month':     { const m = subMonths(now, 1); return { start: startOfMonth(m), end: endOfMonth(m) }; }
    case 'lastmonth': { const m = subMonths(now, 2); return { start: startOfMonth(m), end: endOfMonth(m) }; }
    case 'quarter': {
      const qm = Math.floor(now.getMonth() / 3) * 3;
      const ps = new Date(now.getFullYear(), qm - 3, 1);
      return { start: ps, end: new Date(now.getFullYear(), qm, 0, 23, 59, 59) };
    }
    case 'year': { const y = subYears(now, 1); return { start: startOfYear(y), end: endOfYear(y) }; }
    case 'custom': {
      const cr = getRange(period, cs, ce); const dur = cr.end - cr.start;
      return { start: new Date(cr.start.getTime() - dur - 1), end: new Date(cr.start.getTime() - 1) };
    }
    default: { const m = subMonths(now, 1); return { start: startOfMonth(m), end: endOfMonth(m) }; }
  }
};

const inRange = (date, range) => date && date >= range.start && date <= range.end;

const getIncome = (inv) => {
  if (inv.type !== 'income') return 0;
  if (inv.isInsuranceSettlement) return parseFloat(inv.amount || 0);
  if (inv.insuranceMode === 'direct') return parseFloat(inv.franchigia || 0);
  return parseFloat(inv.amount || 0);
};
const getPending = (inv) => {
  if (inv.type !== 'income' || inv.isInsuranceSettlement) return 0;
  if (inv.insuranceMode === 'direct' && !inv.insurancePaid)
    return parseFloat(inv.amount || 0) - parseFloat(inv.franchigia || 0);
  return 0;
};
const getOutcome = (inv) =>
  (inv.type === 'refund' || inv.type === 'outcome') ? parseFloat(inv.amount || 0) : 0;

// 🎯 FIX 2: IL NUOVO MOTORE DI CALCOLO COMPENSI E PRODUZIONE
// Legge direttamente l'appuntamento (e i suoi services esplosi) applicando Pacchetti e Combo!
const getAppointmentProductionAndComp = (appt, targetDoctorId) => {
    let prod = 0;
    let comp = 0;
    // Escludiamo annullati o non completati/pagati
    if (appt.status === 'cancelled' || appt.waConfirmationStatus === 'cancelled') return { prod, comp };
    
    // Fallback per appuntamenti vecchissimi senza array services
    const services = appt.services && appt.services.length > 0 ? appt.services : [{
        id: appt.serviceId, serviceId: appt.serviceId, price: appt.price || 0, doctorId: appt.doctorId
    }];

    services.forEach(srv => {
        // Se stiamo calcolando per un medico specifico, saltiamo le voci di altri medici
        if (targetDoctorId && srv.doctorId !== targetDoctorId && appt.doctorId !== targetDoctorId) return;
        
        // 1. Calcolo Base Reale
        let basePrice = parseFloat(srv.price || 0);
        if (srv.isPartOfCombo) basePrice = parseFloat(srv.finalPrice || srv.price || 0);
        if (srv.isCoveredByPackage) basePrice = parseFloat(srv.gettoneMedico || srv.price || 0);
        
        // 2. Calcolo Percentuale
        let pct = parseFloat(srv.doctorPercentage) || parseFloat(srv.percentage) || 50; // Fallback
        
        prod += basePrice;
        comp += (basePrice * (pct / 100));
    });
    return { prod, comp };
};

const calcDelta = (curr, prev) => {
  if (!prev || prev === 0) return null;
  const pct = ((curr - prev) / prev) * 100;
  return { pct: Math.round(pct * 10) / 10, positive: pct >= 0 };
};

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).join(';');
  const body = rows.map(r => Object.values(r).map(v => `"${v ?? ''}"`).join(';')).join('\n');
  const blob = new Blob([headers + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── PURE HELPERS — HR ────────────────────────────────────────────────────────
const calculateStaffCosts = (summary, staffMap) => {
  const staff = staffMap[summary.userId];
  if (!staff?.contract) return 0;
  const { contract } = staff;
  const hours    = summary.totalWorkedHours || 0;
  const overtime = summary.overtimeHours    || 0;
  const expenses = summary.totalExpenses    || 0;
  const baseCost     = hours * (contract.hourlyGross || 0);
  const overtimeCost = overtime * (contract.overtimeRate || 0);
  const totalGross   = baseCost + overtimeCost;
  const companyCost  = totalGross * (1 + (contract.taxPercentage || 0) / 100);
  return summary.realCost ?? (companyCost + expenses);
};

const calculatePunctualityMetrics = (summaries) => {
  const userMetrics = {};
  summaries.forEach(summ => {
    if (!userMetrics[summ.userId]) {
      userMetrics[summ.userId] = {
        name: summ.userName,
        totalShifts: 0, lateArrivals: 0, earlyDepartures: 0,
        totalMinutesLate: 0, totalMinutesEarly: 0,
      };
    }
    const m = userMetrics[summ.userId];
    if (summ.punctualityData) {
      m.totalShifts       += summ.punctualityData.totalShifts       || 0;
      m.lateArrivals      += summ.punctualityData.lateArrivals      || 0;
      m.earlyDepartures   += summ.punctualityData.earlyDepartures   || 0;
      m.totalMinutesLate  += summ.punctualityData.totalMinutesLate  || 0;
      m.totalMinutesEarly += summ.punctualityData.totalMinutesEarly || 0;
    }
  });
  return Object.values(userMetrics).map(m => ({
    ...m,
    punctualityRate: m.totalShifts > 0
      ? ((m.totalShifts - m.lateArrivals) / m.totalShifts) * 100
      : 100,
    avgMinutesLate: m.lateArrivals > 0 ? m.totalMinutesLate / m.lateArrivals : 0,
  }));
};

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

const DeltaBadge = ({ delta }) => {
  if (!delta) return null;
  const Icon = delta.positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '2px',
      fontSize: '0.73rem', fontWeight: '700', padding: '2px 8px',
      borderRadius: '999px',
      background: delta.positive ? '#dcfce7' : '#fee2e2',
      color:      delta.positive ? '#166534' : '#991b1b',
    }}>
      <Icon size={11}/>{Math.abs(delta.pct)}%
    </span>
  );
};

const Card = ({ children, style = {}, noPad = false }) => (
  <div style={{
    background: 'white', borderRadius: '16px',
    padding: noPad ? 0 : '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05),0 4px 16px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', overflow: 'hidden', ...style,
  }}>
    {children}
  </div>
);

const SectionTitle = ({ children, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
    <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>{children}</h3>
    {action}
  </div>
);

const KPICard = ({ label, value, sub, delta, icon, color, isCurrency = true }) => (
  <div style={{
    background: 'white', borderRadius: '16px', padding: '1.4rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05),0 4px 16px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', flex: 1, minWidth: '160px',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '12px',
        background: color + '18', display: 'flex', alignItems: 'center',
        justifyContent: 'center', color,
      }}>{icon}</div>
      <DeltaBadge delta={delta}/>
    </div>
    <div style={{ fontSize: '1.55rem', fontWeight: '700', color: '#0f172a', lineHeight: 1.1, marginBottom: '4px', letterSpacing: '-0.02em' }}>
      {isCurrency ? fmtShort(value) : (value || 0).toLocaleString('it-IT')}
    </div>
    <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#475569', marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>{sub}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem',
    }}>
      <div style={{ fontWeight: '700', marginBottom: '6px', color: '#0f172a' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
          <span style={{ color: '#64748b' }}>{p.name}</span>
          <span style={{ fontWeight: '700', color: p.color }}>
            {typeof p.value === 'number' && p.value > 100
              ? `€ ${p.value.toLocaleString('it-IT')}`
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ icon, text }) => (
  <div style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>
    <div style={{ marginBottom: '8px', opacity: 0.4 }}>{icon}</div>
    <div style={{ fontSize: '0.85rem' }}>{text}</div>
  </div>
);

// ─── DRILL-DOWN MODAL (HR) ────────────────────────────────────────────────────
const DrillDownModal = ({ isOpen, onClose, title, data, type }) => {
  if (!isOpen) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '16px', width: '90%', maxWidth: '680px',
          maxHeight: '82vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg,#1d4ed8 0%,#7c3aed 100%)',
          borderRadius: '16px 16px 0 0',
        }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'white' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
              fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {type === 'monthly' && data && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                { label: 'Ore Pianificate',  value: `${(data.plannedHours  || 0).toFixed(1)} h` },
                { label: 'Ore Lavorate',     value: `${(data.workedHours   || 0).toFixed(1)} h` },
                { label: 'Straordinari',     value: `${(data.overtimeHours || 0).toFixed(1)} h` },
                { label: 'Assenze',          value: `${data.absences        || 0} giorni` },
                { label: 'Costi Staff',      value: `€ ${(data.staffCost   || 0).toFixed(2)}` },
                { label: 'Costi Medici',     value: `€ ${(data.doctorCost  || 0).toFixed(2)}` },
              ].map((item, i) => (
                <div key={i} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', borderLeft: '3px solid #1d4ed8' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>{item.value}</div>
                </div>
              ))}
            </div>
          )}
          {type === 'room' && data && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                { label: 'Ore Occupazione', value: `${data.minutes} h` },
                { label: 'Tasso Utilizzo',  value: `${((data.minutes / 720) * 100).toFixed(1)}%` },
              ].map((item, i) => (
                <div key={i} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', borderLeft: '3px solid #0891b2' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>{item.value}</div>
                </div>
              ))}
              <div style={{ gridColumn: '1/-1', background: '#eff6ff', borderLeft: '4px solid #0891b2', padding: '12px', borderRadius: '8px', fontSize: '0.85rem', color: '#1e40af' }}>
                💡 Calcolato su 720h mensili disponibili (30 giorni × 24h)
              </div>
            </div>
          )}
          {type === 'absence' && data && (
            <div>
              <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', borderLeft: '3px solid #d97706', marginBottom: '14px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Totale Giorni</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1d4ed8' }}>{data.value}</div>
              </div>
              <div style={{ background: '#fffbeb', borderLeft: '4px solid #d97706', padding: '12px', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                {data.label === 'Malattia'  && '🏥 Include certificati medici e giorni di malattia'}
                {data.label === 'Ferie'     && '🌴 Ferie godute nel periodo selezionato'}
                {data.label === 'Permessi'  && '📅 Permessi retribuiti e ROL'}
              </div>
            </div>
          )}
          {type === 'product' && data && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                { label: 'Quantità Consumata', value: data.quantity },
                { label: 'Valore Totale',      value: `€ ${data.value?.toFixed(2)}` },
              ].map((item, i) => (
                <div key={i} style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', borderLeft: '3px solid #059669' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>{item.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', background: '#f1f5f9', border: 'none',
              borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
              color: '#475569', fontSize: '0.875rem',
            }}
          >Chiudi</button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ReportsPage() {

  // ── OUTER TAB ──────────────────────────────────────────────────────────────
  const [outerTab, setOuterTab] = useState('performance'); // 'performance' | 'hr'

  // AGGIUNGI:
const { userProfile } = useUser();
const centerId = userProfile?.centerId;

  // ── PERFORMANCE STATE ──────────────────────────────────────────────────────
  const [invoices,          setInvoices]          = useState([]);
  const [doctors,           setDoctors]           = useState([]);
  const [patients,          setPatients]          = useState([]);
  const [acconti,           setAcconti]           = useState([]);
  
  const [services,          setServices]          = useState([]);
  const [appointments,      setAppointments]      = useState([]);
  const [rooms,             setRooms]             = useState([]);
  const [purchasedPackages, setPurchasedPackages] = useState([]);
  const [perfLoading,       setPerfLoading]       = useState(true);

  const [perfTab,     setPerfTab]     = useState('dashboard');
  const [period,      setPeriod]      = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [selectedDoc, setSelectedDoc] = useState('all');

  // ── HR STATE ───────────────────────────────────────────────────────────────
  const [hrTab,       setHrTab]       = useState('hr');
  const [hrLoading,   setHrLoading]   = useState(false);
  const [year,        setYear]        = useState(moment().year());
  const [startMonth,  setStartMonth]  = useState(0);
  const [endMonth,    setEndMonth]    = useState(11);
  const [filterRole,  setFilterRole]  = useState('all');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [drillDownModal, setDrillDownModal] = useState({ isOpen: false, title: '', data: null, type: '' });

  const [rawSummaries,          setRawSummaries]          = useState([]);
  const [rawTallies,            setRawTallies]            = useState([]);
  const [rawAssignments,        setRawAssignments]        = useState([]);
  const [staffMap,              setStaffMap]              = useState({});
  const [roomMap,               setRoomMap]               = useState({});
  const [equipmentMap,          setEquipmentMap]          = useState({});
  const [rawInventory,          setRawInventory]          = useState([]);
  const [rawInventoryMovements, setRawInventoryMovements] = useState([]);
  const [productMap,            setProductMap]            = useState({});

  // ── PERFORMANCE — DATA FETCH ───────────────────────────────────────────────
  useEffect(() => {
    if (!centerId) return;
    setPerfLoading(true);
    Promise.all([
      getDocs(collection(db, "centers", centerId, "invoices")),
      getDocs(collection(db, "centers", centerId, "doctors")),
      getDocs(collection(db, "centers", centerId, "patients")),
      getDocs(collection(db, "centers", centerId, "services")),
      getDocs(collection(db, "centers", centerId, "appointments")),
      getDocs(collection(db, "centers", centerId, "rooms")),
      getDocs(collection(db, "centers", centerId, "purchased_packages")),
      getDocs(collection(db, "centers", centerId, "acconti")),
    ]).then(([invS, docS, patS, svcS, appS, roomS, pkgS, accS]) => {
      setInvoices(invS.docs.map(d => ({
        id: d.id, ...d.data(),
        date: parseDate(d.data().date) ?? new Date(0),
      })));
      setDoctors(docS.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, nome: data.firstName || data.nome || "Dr.", cognome: data.lastName || data.cognome || "" };
      }));
      setPatients(patS.docs.map(d => ({ id: d.id, ...d.data() })));
      setServices(svcS.docs.map(d => ({ id: d.id, ...d.data() })));
      setAppointments(appS.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, start: parseDate(data.start) ?? new Date(0), end: parseDate(data.end) ?? new Date(0) };
      }));
      setRooms(roomS.docs.map(d => ({ id: d.id, ...d.data(), nome: d.data().name || d.data().nome })));
      setPurchasedPackages(pkgS.docs.map(d => ({ id: d.id, ...d.data() })));
      setAcconti(accS.docs.map(d => ({ id: d.id, ...d.data() })));
      setPerfLoading(false);
    }).catch(e => { console.error("Errore fetch Performance:", e); setPerfLoading(false); });
  }, [centerId]);

  // ── HR — DATA FETCH ────────────────────────────────────────────────────────
  const fetchHRData = async () => {
    if (!centerId) return;
    setHrLoading(true);
    try {
      const startStr = moment({ year, month: startMonth }).startOf('month').format('YYYY-MM');
      const endStr   = moment({ year, month: endMonth   }).endOf('month').format('YYYY-MM');

      const staffSnap = await getDocs(collection(db, 'centers', centerId, 'staff'));
      const sMap = {};
      const roles = new Set();
      staffSnap.docs.forEach(d => {
        const data = d.data();
        sMap[d.id] = {
          id: d.id, name: data.name, role: data.role || 'Staff',
          contract: {
            hourlyGross:    Number(data.contractData?.hourlyGrossCost) || 0,
            overtimeRate:   Number(data.contractData?.overtimeRate)    || 0,
            taxPercentage:  Number(data.contractData?.taxPercentage)   || 0,
          },
          leaveBalances: data.leaveBalances || {},
        };
        roles.add(data.role || 'Staff');
      });
      setStaffMap(sMap);
      setAvailableRoles(Array.from(roles));

      const summSnap = await getDocs(query(
        collection(db, 'centers', centerId, 'monthlySummaries'),
        where('monthYear', '>=', startStr),
        where('monthYear', '<=', endStr),
      ));
      setRawSummaries(summSnap.docs.map(d => d.data()));

      const talliesSnap = await getDocs(query(
        collection(db, 'centers', centerId, 'doctorTallies'),
        where('monthYear', '>=', startStr),
        where('monthYear', '<=', endStr),
        where('status', '==', 'approved'),
      ));
      setRawTallies(talliesSnap.docs.map(d => d.data()));

      if (hrTab === 'rooms') {
        const [rSnap, eSnap] = await Promise.all([
          getDocs(collection(db, 'centers', centerId, 'rooms')),
          getDocs(collection(db, 'centers', centerId, 'equipment')),
        ]);
        const rMap = {}; rSnap.docs.forEach(d => (rMap[d.id] = { id: d.id, ...d.data() }));
        setRoomMap(rMap);
        const eMap = {}; eSnap.docs.forEach(d => (eMap[d.id] = { id: d.id, ...d.data() }));
        setEquipmentMap(eMap);

        const assignSnap = await getDocs(query(
          collection(db, 'centers', centerId, 'roomAssignments'),
          where('validUntil', '>=', `${year}-01-01`),
        ));
        const startD = moment({ year, month: startMonth }).startOf('month');
        const endD   = moment({ year, month: endMonth   }).endOf('month');
        setRawAssignments(assignSnap.docs.map(d => d.data()).filter(a => {
          const aStart = moment(a.validFrom);
          const aEnd   = moment(a.validUntil);
          return aStart.isSameOrBefore(endD) && aEnd.isSameOrAfter(startD);
        }));
      }

      if (hrTab === 'inventory') {
        const [invSnap, movSnap] = await Promise.all([
          getDocs(collection(db, 'centers', centerId, 'inventory')),
          getDocs(query(
            collection(db, 'centers', centerId, 'inventoryMovements'),
            where('date', '>=', `${year}-${String(startMonth + 1).padStart(2, '0')}-01`),
            where('date', '<=', moment({ year, month: endMonth }).endOf('month').format('YYYY-MM-DD')),
          )),
        ]);
        const invMap = {}; const invData = [];
        invSnap.docs.forEach(d => { const data = { id: d.id, ...d.data() }; invMap[d.id] = data; invData.push(data); });
        setProductMap(invMap);
        setRawInventory(invData);
        setRawInventoryMovements(movSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error("Errore fetch HR report:", err);
    } finally {
      setHrLoading(false);
    }
  };

  useEffect(() => {
    if (outerTab === 'hr') fetchHRData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerId, year, startMonth, endMonth, hrTab, outerTab]);

  // ── PERFORMANCE — DERIVED DATA ─────────────────────────────────────────────
  const svcByName = useMemo(() => {
    const m = {}; services.forEach(s => { m[s.nome] = s; }); return m;
  }, [services]);

  const currRange = useMemo(() => getRange(period, customStart, customEnd), [period, customStart, customEnd]);
  const prevRange = useMemo(() => getPrevRange(period, customStart, customEnd), [period, customStart, customEnd]);

  const currInv  = useMemo(() => invoices.filter(i => inRange(i.date, currRange)),       [invoices, currRange]);
  const prevInv  = useMemo(() => invoices.filter(i => inRange(i.date, prevRange)),       [invoices, prevRange]);
  const currAppt = useMemo(() => appointments.filter(a => inRange(a.start, currRange)),  [appointments, currRange]);
  const prevAppt = useMemo(() => appointments.filter(a => inRange(a.start, prevRange)),  [appointments, prevRange]);

  const kpis = useMemo(() => {
    const inc  = currInv.reduce((s, i) => s + getIncome(i), 0);
    const pInc = prevInv.reduce((s, i) => s + getIncome(i), 0);
    const pend = currInv.reduce((s, i) => s + getPending(i), 0);
    // 🎯 FIX 3A: Calcolo compensi globali basato sul Lavoro (Appuntamenti completati) e non sulle fatture!
    const comp = currAppt.filter(a => a.status === 'completed' || a.status === 'accepted' || a.paid).reduce((s, a) => {
        return s + getAppointmentProductionAndComp(a, null).comp;
    }, 0);
    const out  = currInv.reduce((s, i) => s + getOutcome(i), 0);
    // 🎯 NUOVO: Calcolo Appuntamenti Fantasma (No-Show e Non Fatturati)
    let persiNoShow = 0;
    let creditiNonFatturati = 0;
    const now = new Date();

    currAppt.forEach(a => {
        // Se l'appuntamento è nel passato e non è stato annullato esplicitamente
        if (a.start < now && a.status !== 'cancelled') {
            // Cerca se esiste una fattura collegata
            const hasInvoice = currInv.some(inv => 
                inv.appointmentId === a.id || 
                inv.id === a.invoiceId || 
                (inv.items && inv.items.some(item => item.appointmentId === a.id || item.id === a.id))
            );

            if (!hasInvoice) {
                const price = parseFloat(a.price || 0);
                // Se manca accettazione -> No-Show
                if (a.status === 'scheduled' || a.status === 'pending' || !a.status) {
                    persiNoShow += price;
                } 
                // Se c'è accettazione ma niente fattura -> Credito Nascosto
                else if (a.status === 'accepted' || a.status === 'completed') {
                    creditiNonFatturati += price;
                }
            }
        }
    });
    const patIds     = new Set(currAppt.map(a => a.patientId).filter(Boolean));
    const prevPatIds = new Set(prevAppt.map(a => a.patientId).filter(Boolean));
    const billable = currInv.filter(i => i.type === 'income' && !i.isCustomMovement && !i.isInsuranceSettlement);
    const ticket   = billable.length > 0 ? billable.reduce((s, i) => s + parseFloat(i.amount || 0), 0) / billable.length : 0;
    const prevBill = prevInv.filter(i => i.type === 'income' && !i.isCustomMovement && !i.isInsuranceSettlement);
    const prevTick = prevBill.length > 0 ? prevBill.reduce((s, i) => s + parseFloat(i.amount || 0), 0) / prevBill.length : 0;
    return {
      income:   { value: inc,              delta: calcDelta(inc, pInc),                  label: 'Incassato netto',   sub: `Sospesi: ${fmt(pend)}` },
      appts:    { value: currAppt.length,  delta: calcDelta(currAppt.length, prevAppt.length), label: 'Appuntamenti', sub: `${doctors.length} medici attivi` },
      patients: { value: patIds.size,      delta: calcDelta(patIds.size, prevPatIds.size),     label: 'Pazienti attivi', sub: `${patients.length} in archivio` },
      ticket:   { value: ticket,           delta: calcDelta(ticket, prevTick),            label: 'Ticket medio',     sub: `su ${billable.length} fatture` },
      comp:     { value: comp,             delta: null,                                   label: 'Compensi medici',  sub: `Quota struttura: ${fmtShort(inc - out - comp)}` },
    // 🎯 NUOVI KPI
      lostNoShow:  { value: persiNoShow,      delta: null, label: 'Persi per No-Show', sub: 'Pazienti non presentati' },
      uninvoiced:  { value: creditiNonFatturati, delta: null, label: 'Da Fatturare', sub: 'Sedute erogate senza doc.' },
    };
  }, [currInv, prevInv, currAppt, prevAppt, svcByName, doctors, patients]);

  const trend12 = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = subMonths(now, 11 - i);
      const s = startOfMonth(d), e = endOfMonth(d);
      const mo = invoices.filter(inv => inv.date >= s && inv.date <= e);
      return {
        month:   MONTHS[d.getMonth()],
        Entrate: Math.round(mo.reduce((sum, i) => sum + getIncome(i),  0)),
        Uscite:  Math.round(mo.reduce((sum, i) => sum + getOutcome(i), 0)),
      };
    });
  }, [invoices]);

  const payMethods = useMemo(() => {
    const m = {};
    currInv.filter(i => i.type === 'income').forEach(inv => {
      const k = inv.method || 'altro';
      m[k] = (m[k] || 0) + getIncome(inv);
    });
    return Object.entries(m).map(([method, value]) => ({
      name: METHOD_LABELS[method] || method,
      value: Math.round(value * 100) / 100,
    }));
  }, [currInv]);

  const topServices = useMemo(() => {
    const m = {};
    currInv.filter(i => i.type === 'income' && !i.isCustomMovement).forEach(inv => {
      (inv.items || []).forEach(item => { m[item.name] = (m[item.name] || 0) + parseFloat(item.price || 0); });
    });
    return Object.entries(m).map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value).slice(0, 7);
  }, [currInv]);

  const doctorStats = useMemo(() => {
    const base = {};
    doctors.forEach(d => { base[d.id] = { id: d.id, name: `${d.cognome} ${d.nome}`, fat: 0, prod: 0, comp: 0, strutt: 0, items: 0 }; });
    
    // 1. FATTURATO (Cash Flow Cassa) -> Dalle Fatture
    currInv.filter(i => i.type === 'income' && !i.isCustomMovement && !i.isInsuranceSettlement).forEach(inv => {
        if (inv.doctorId && base[inv.doctorId]) {
            base[inv.doctorId].fat += parseFloat(inv.amount || 0);
        }
    });

    // 2. PRODUZIONE & COMPENSI -> Dal Lavoro Reale (Appuntamenti)
    currAppt.filter(a => a.status === 'completed' || a.status === 'accepted' || a.paid).forEach(app => {
        const srvs = app.services && app.services.length > 0 ? app.services : [{ doctorId: app.doctorId }];
        const docIds = [...new Set(srvs.map(s => s.doctorId || app.doctorId))];
        
        docIds.forEach(dId => {
            if (base[dId]) {
                const { prod, comp } = getAppointmentProductionAndComp(app, dId);
                base[dId].prod += prod;
                base[dId].comp += comp;
                base[dId].strutt += (prod - comp); // Quota struttura basata sulla Produzione
                base[dId].items += 1;
            }
        });
    });

    return Object.values(base).sort((a, b) => b.prod - a.prod); // Ora ordiniamo per chi ha PRODOTTO di più!
  }, [currInv, currAppt, doctors]);

  const docTrend6 = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = subMonths(now, 5 - i);
      const s = startOfMonth(d), e = endOfMonth(d);
      const entry = { month: MONTHS[d.getMonth()] };
      doctors.forEach(doc => {
        const mo = invoices.filter(inv =>
          inv.date >= s && inv.date <= e &&
          inv.doctorId === doc.id &&
          inv.type === 'income' && !inv.isCustomMovement && !inv.isInsuranceSettlement
        );
        entry[doc.cognome || doc.nome] = Math.round(mo.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0));
      });
      return entry;
    });
  }, [invoices, doctors]);

  const incassiStats = useMemo(() => ({
    income:  currInv.reduce((s, i) => s + getIncome(i),  0),
    outcome: currInv.reduce((s, i) => s + getOutcome(i), 0),
    pending: invoices.reduce((s, i) => s + getPending(i), 0),
    prevInc: prevInv.reduce((s, i) => s + getIncome(i),  0),
    prevOut: prevInv.reduce((s, i) => s + getOutcome(i), 0),
  }), [currInv, prevInv, invoices]);

  const catBreakdown = useMemo(() => {
    const m = {};
    currInv.filter(i => i.isCustomMovement).forEach(inv => {
      const cat = inv.categoryName || 'Senza categoria';
      if (!m[cat]) m[cat] = { income: 0, outcome: 0 };
      if (inv.type === 'income') m[cat].income  += parseFloat(inv.amount || 0);
      else                        m[cat].outcome += parseFloat(inv.amount || 0);
    });
    return Object.entries(m).map(([name, v]) => ({ name, ...v }));
  }, [currInv]);

  const topPatients = useMemo(() => {
    const m = {};
    currInv.filter(i => i.type === 'income' && i.patientId && !i.isCustomMovement).forEach(inv => {
      if (!m[inv.patientId]) m[inv.patientId] = { name: inv.patientName || 'N/D', fat: 0, n: 0 };
      m[inv.patientId].fat += parseFloat(inv.amount || 0);
      m[inv.patientId].n   += 1;
    });
    return Object.values(m).sort((a, b) => b.fat - a.fat).slice(0, 8);
  }, [currInv]);

  const docServiceBreakdown = useMemo(() => {
    if (selectedDoc === 'all') return [];
    const m = {};
    currInv.filter(i => i.doctorId === selectedDoc && i.type === 'income' && !i.isCustomMovement && !i.isInsuranceSettlement)
      .forEach(inv => {
        (inv.items || []).forEach(item => {
          if (!m[item.name]) m[item.name] = { name: item.name, count: 0, tot: 0 };
          m[item.name].count += 1;
          m[item.name].tot   += parseFloat(item.price || 0);
        });
      });
    return Object.values(m).sort((a, b) => b.tot - a.tot).slice(0, 8);
  }, [currInv, selectedDoc]);

  const insuranceStats = useMemo(() => {
    const m = {};
    invoices.filter(i => i.insuranceMode === 'direct' && i.type === 'income' && !i.isInsuranceSettlement)
      .forEach(inv => {
        const key   = inv.insuranceId   || inv.insuranceName || 'Senza nome';
        const label = inv.insuranceName || key;
        if (!m[key]) m[key] = { id: key, name: label, fatturato: 0, pendingAmt: 0, pendingCount: 0, franchigie: 0, settledAmt: 0, pratiche: [] };
        const tot = parseFloat(inv.amount    || 0);
        const fra = parseFloat(inv.franchigia || 0);
        m[key].fatturato  += tot;
        m[key].franchigie += fra;
        if (!inv.insurancePaid) { m[key].pendingAmt += (tot - fra); m[key].pendingCount += 1; m[key].pratiche.push({ ...inv }); }
        else                    { m[key].settledAmt += (tot - fra); }
      });
    return Object.values(m).sort((a, b) => b.pendingAmt - a.pendingAmt);
  }, [invoices]);

  const oldestPending = useMemo(() => {
    const all = [];
    invoices.filter(i => i.insuranceMode === 'direct' && i.type === 'income' && !i.isInsuranceSettlement && !i.insurancePaid)
      .forEach(inv => { const daysAgo = Math.floor((new Date() - inv.date) / (1000 * 60 * 60 * 24)); all.push({ ...inv, daysAgo }); });
    return all.sort((a, b) => b.daysAgo - a.daysAgo).slice(0, 10);
  }, [invoices]);

  const insPeriodStats = useMemo(() => {
    const m = {};
    currInv.filter(i => i.insuranceMode === 'direct' && i.type === 'income' && !i.isInsuranceSettlement)
      .forEach(inv => {
        const key = inv.insuranceId || inv.insuranceName || 'N/D';
        if (!m[key]) m[key] = { name: inv.insuranceName || key, fatturato: 0, franchigie: 0, pending: 0 };
        m[key].fatturato  += parseFloat(inv.amount     || 0);
        m[key].franchigie += parseFloat(inv.franchigia  || 0);
        if (!inv.insurancePaid) m[key].pending += (parseFloat(inv.amount || 0) - parseFloat(inv.franchigia || 0));
      });
    return Object.values(m).sort((a, b) => b.fatturato - a.fatturato);
  }, [currInv]);

  const newPatientsByMonth = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = subMonths(now, 11 - i);
      const s = startOfMonth(d), e = endOfMonth(d);
      const count = patients.filter(p => { const cd = parseDate(p.createdAt); return cd && cd >= s && cd <= e; }).length;
      return { month: MONTHS[d.getMonth()], Nuovi: count };
    });
  }, [patients]);

  const patientActivity = useMemo(() => {
    const INACTIVE_DAYS = 90;
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - INACTIVE_DAYS);
    const lastAppt = {};
    appointments.forEach(a => { if (!a.patientId) return; if (!lastAppt[a.patientId] || a.start > lastAppt[a.patientId]) lastAppt[a.patientId] = a.start; });
    let active = 0, inactive = 0, never = 0;
    patients.forEach(p => {
      if (!lastAppt[p.id])                    { never++;    return; }
      if (lastAppt[p.id] >= cutoff)            { active++;  } else { inactive++; }
    });
    return [
      { name: 'Attivi',          value: active,   color: '#059669' },
      { name: 'Inattivi >90gg',  value: inactive, color: '#f59e0b' },
      { name: 'Mai venuti',       value: never,    color: '#e2e8f0' },
    ];
  }, [patients, appointments]);

  const patientGender = useMemo(() => {
    const m = { M: 0, F: 0, Altro: 0 };
    patients.forEach(p => {
      if      (p.sesso === 'Maschile'  || p.sesso === 'M') m.M++;
      else if (p.sesso === 'Femminile' || p.sesso === 'F') m.F++;
      else m.Altro++;
    });
    return [
      { name: 'Uomo',  value: m.M,     color: '#1d4ed8' },
      { name: 'Donna', value: m.F,     color: '#db2777' },
      { name: 'Altro', value: m.Altro, color: '#94a3b8' },
    ];
  }, [patients]);

  const avgApptsPerPatient = useMemo(() => {
    if (!patients.length) return 0;
    const counts = {};
    appointments.forEach(a => { if (a.patientId) counts[a.patientId] = (counts[a.patientId] || 0) + 1; });
    return Object.values(counts).reduce((s, v) => s + v, 0) / patients.length;
  }, [patients, appointments]);

  const expiringPackages = useMemo(() => {
    const now = new Date(); const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    return purchasedPackages.filter(pkg => {
      if (pkg.status === 'completed') return false;
      if (pkg.endDate) { const ed = parseDate(pkg.endDate); return ed && ed <= in30 && ed >= now; }
      if (pkg.sessionsLeft !== undefined) return pkg.sessionsLeft <= 2 && pkg.sessionsLeft > 0;
      return false;
    }).map(pkg => ({ ...pkg, endDateParsed: pkg.endDate ? parseDate(pkg.endDate) : null }))
      .sort((a, b) => {
        if (a.endDateParsed && b.endDateParsed) return a.endDateParsed - b.endDateParsed;
        return (a.sessionsLeft || 99) - (b.sessionsLeft || 99);
      }).slice(0, 10);
  }, [purchasedPackages]);

  const newPatientsInPeriod = useMemo(() =>
    patients.filter(p => { const cd = parseDate(p.createdAt); return cd && inRange(cd, currRange); }).length,
  [patients, currRange]);

  const roomOccupancy = useMemo(() => {
    if (!rooms.length) return [];
    const counts = {};
    rooms.forEach(r => { counts[r.id] = { name: r.nome, total: 0 }; });
    currAppt.forEach(a => { if (a.roomId && counts[a.roomId]) counts[a.roomId].total++; });
    const max = Math.max(...Object.values(counts).map(v => v.total), 1);
    return Object.values(counts).map(r => ({ ...r, pct: Math.round((r.total / max) * 100) })).sort((a, b) => b.total - a.total);
  }, [rooms, currAppt]);

  const hoursPerDoctor = useMemo(() => {
    const m = {};
    doctors.forEach(d => { m[d.id] = { name: `${d.cognome} ${d.nome}`, minutes: 0, appts: 0 }; });
    currAppt.forEach(a => {
      if (!a.doctorId || !m[a.doctorId]) return;
      const dur = a.end && a.start ? (a.end - a.start) / (1000 * 60) : 0;
      m[a.doctorId].minutes += dur; m[a.doctorId].appts += 1;
    });
    return Object.values(m).filter(d => d.appts > 0)
      .map(d => ({ ...d, hours: Math.round(d.minutes / 60 * 10) / 10 }))
      .sort((a, b) => b.hours - a.hours);
  }, [currAppt, doctors]);

  const heatmapData = useMemo(() => {
    const grid = {}; const DAYS = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
    DAYS.forEach(d => { grid[d] = {}; for (let h = 7; h <= 20; h++) grid[d][h] = 0; });
    appointments.forEach(a => {
      const dw = (a.start.getDay() + 6) % 7; const h = a.start.getHours();
      if (h < 7 || h > 20) return;
      grid[DAYS[dw]][h] = (grid[DAYS[dw]][h] || 0) + 1;
    });
    const flat = []; DAYS.forEach(day => { for (let h = 7; h <= 20; h++) flat.push({ day, hour: `${h}:00`, value: grid[day][h] || 0 }); });
    return { grid, days: DAYS, flat };
  }, [appointments]);

  const busyDays = useMemo(() => {
    const m = {};
    currAppt.forEach(a => { const k = a.start.toLocaleDateString('it-IT'); m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([date, count]) => ({ date, count })).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [currAppt]);

  const apptsByWeekday = useMemo(() => {
    const DAYS = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
    const counts = Array(7).fill(0);
    appointments.forEach(a => { counts[(a.start.getDay() + 6) % 7]++; });
    return DAYS.map((d, i) => ({ giorno: d, Appuntamenti: counts[i] }));
  }, [appointments]);

  // ── PERFORMANCE — ADVANCED METRICS ────────────────────────────────────────

// 1. No-Show & WhatsApp efficacy
const noShowStats = useMemo(() => {
  const total = currAppt.length;
  if (!total) return null;
  const counts = { confirmed: 0, cancelled: 0, to_reschedule: 0, pending: 0, none: 0 };
  currAppt.forEach(a => {
    const s = a.waStatus || a.confirmationStatus || 'none';
    if (counts[s] !== undefined) counts[s]++;
    else counts.none++;
  });
  const noShow     = counts.cancelled + counts.to_reschedule;
  const saved      = counts.confirmed;
  const noShowRate = total > 0 ? Math.round((noShow / total) * 100) : 0;
  const saveRate   = total > 0 ? Math.round((saved / total) * 100)  : 0;
  return {
    total, noShow, saved, noShowRate, saveRate,
    pending: counts.pending,
    chartData: [
      { name: '✅ Confermati',      value: counts.confirmed,    color: '#059669' },
      { name: '❌ Cancellati',      value: counts.cancelled,    color: '#dc2626' },
      { name: '🔄 Da riprogrammare',value: counts.to_reschedule,color: '#d97706' },
      { name: '⏳ In attesa',       value: counts.pending,      color: '#94a3b8' },
      { name: '—  Nessun dato',     value: counts.none,         color: '#e2e8f0' },
    ].filter(d => d.value > 0),
  };
}, [currAppt]);

// 2. Room saturation & revenue per room
const roomRevenueStats = useMemo(() => {
  if (!rooms.length) return [];
  // minuti di slot disponibili nel periodo (stima: giorni lavorativi × 9h)
  const rangeMs   = currRange.end - currRange.start;
  const rangeDays = Math.max(1, Math.round(rangeMs / (1000 * 60 * 60 * 24)));
  const workDays  = Math.round(rangeDays * 5 / 7);
  const availMins = workDays * 9 * 60; // 9h/giorno

  const roomData = {};
  rooms.forEach(r => {
    roomData[r.id] = { id: r.id, name: r.nome, totalMins: 0, revenue: 0, appts: 0 };
  });

  currAppt.forEach(a => {
    if (!a.roomId || !roomData[a.roomId]) return;
    const dur = a.end && a.start ? (a.end - a.start) / (1000 * 60) : 0;
    roomData[a.roomId].totalMins += dur;
    roomData[a.roomId].appts     += 1;
    // cerca la fattura collegata all'appuntamento
    const relInv = currInv.filter(i =>
      i.type === 'income' && !i.isCustomMovement &&
      (i.appointmentId === a.id || i.patientId === a.patientId)
    );
    // attribuiamo in modo proporzionale se più appuntamenti per paziente
    // semplificazione: sommiamo il ticket medio per appuntamento
  });

  // revenue per stanza: sommiamo le fatture il cui doctorId lavora principalmente in quella stanza
  currInv.filter(i => i.type === 'income' && !i.isCustomMovement && i.roomId)
    .forEach(inv => {
      if (roomData[inv.roomId]) {
        roomData[inv.roomId].revenue += parseFloat(inv.amount || 0);
      }
    });

  return Object.values(roomData)
    .filter(r => r.appts > 0 || r.totalMins > 0)
    .map(r => ({
      ...r,
      saturation:  availMins > 0 ? Math.min(100, Math.round((r.totalMins / availMins) * 100)) : 0,
      revenuePerH: r.totalMins > 0 ? Math.round((r.revenue / (r.totalMins / 60)) * 10) / 10 : 0,
    }))
    .sort((a, b) => b.revenuePerH - a.revenuePerH);
}, [rooms, currAppt, currInv, currRange]);

// 3. Churn rate & LTV
const churnStats = useMemo(() => {
  const patientInvoices = {};
  invoices.filter(i => i.type === 'income' && i.patientId && !i.isCustomMovement)
    .forEach(inv => {
      if (!patientInvoices[inv.patientId])
        patientInvoices[inv.patientId] = { name: inv.patientName || 'N/D', total: 0, count: 0 };
      patientInvoices[inv.patientId].total += parseFloat(inv.amount || 0);
      patientInvoices[inv.patientId].count += 1;
    });

  const all      = Object.values(patientInvoices);
  const oneTime  = all.filter(p => p.count === 1).length;
  const repeat   = all.filter(p => p.count >= 2).length;
  const loyal    = all.filter(p => p.count >= 5).length;
  const totalRev = all.reduce((s, p) => s + p.total, 0);
  const ltv      = all.length > 0 ? Math.round((totalRev / all.length) * 100) / 100 : 0;
  const churnRate= all.length > 0 ? Math.round((oneTime / all.length) * 100) : 0;

  const topLTV = all.sort((a, b) => b.total - a.total).slice(0, 8);

  return {
    oneTime, repeat, loyal, ltv, churnRate,
    total: all.length,
    chartData: [
      { name: 'Prima visita only', value: oneTime, color: '#dc2626' },
      { name: '2–4 visite',        value: repeat - loyal, color: '#d97706' },
      { name: '5+ visite (fidelizzati)', value: loyal, color: '#059669' },
    ].filter(d => d.value > 0),
    topLTV,
  };
}, [invoices]);

// 4. Packages vs single sessions
const packageVsSingle = useMemo(() => {
  const pkgRevenue = purchasedPackages.reduce((s, p) => s + parseFloat(p.totalPrice || p.price || 0), 0);
  const singleInv  = currInv.filter(i =>
    i.type === 'income' && !i.isCustomMovement && !i.packageId && !i.isInsuranceSettlement
  );
  const singleRevenue = singleInv.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const pkgCount    = purchasedPackages.filter(p => {
    const cd = parseDate(p.createdAt || p.purchaseDate);
    return cd && inRange(cd, currRange);
  }).length;
  const singleCount = singleInv.length;
  const total       = pkgRevenue + singleRevenue;

  return {
    pkgRevenue, singleRevenue, pkgCount, singleCount, total,
    pkgPct:    total > 0 ? Math.round((pkgRevenue    / total) * 100) : 0,
    singlePct: total > 0 ? Math.round((singleRevenue / total) * 100) : 0,
    chartData: [
      { name: '📦 Pacchetti',       value: Math.round(pkgRevenue),    color: '#1d4ed8' },
      { name: '🩺 Singole sedute',  value: Math.round(singleRevenue), color: '#0891b2' },
    ].filter(d => d.value > 0),
  };
}, [currInv, purchasedPackages, currRange]);

// 5. Unpaid / credits ranking (MOTORE DEBITI E CREDITI REALE)
const unpaidStats = useMemo(() => {
    const debtsByPatient = {};
    
    const initPatient = (pId, pName) => {
        if (!debtsByPatient[pId]) {
            debtsByPatient[pId] = { patientId: pId, patientName: pName || "Sconosciuto", grossDebt: 0, availableCredit: 0, details: [] };
        }
    };

    // A. FATTURE E PRO-FORMA B2B INSOLUTE
    invoices.filter(i => i.type === 'income' && !i.isCustomMovement && !i.isInsuranceSettlement && !i.voided).forEach(inv => {
        if ((inv.unpaidInvoice === true || (inv.isProForma === true && inv.paid === false)) && inv.patientId) {
            initPatient(inv.patientId, inv.patientName);
            debtsByPatient[inv.patientId].grossDebt += parseFloat(inv.amount || 0);
            debtsByPatient[inv.patientId].details.push(inv.isProForma ? `Pro-Forma B2B` : `Fattura #${inv.number || ''}`);
        }
    });

    // B. SEDUTE EROGATE MA NON PAGATE / FATTURATE
    appointments.filter(a => (a.status === 'in_attesa_di_pagamento' || (!a.paid && a.status === 'completed')) && a.patientId).forEach(a => {
        initPatient(a.patientId, a.patientName);
        debtsByPatient[a.patientId].grossDebt += parseFloat(a.price || 0);
        debtsByPatient[a.patientId].details.push(`Seduta (${format(new Date(a.start), 'dd/MM')})`);
    });

    // C. PACCHETTI ATTIVATI MA NON SALDATI
    purchasedPackages.filter(p => p.paid === false && p.patientId).forEach(p => {
        initPatient(p.patientId, p.patientName);
        debtsByPatient[p.patientId].grossDebt += parseFloat(p.price || p.totalPrice || 0);
        debtsByPatient[p.patientId].details.push(`Pacchetto Iniziato`);
    });

    // D. SOTTRAZIONE ACCONTI (Il vero "Portafoglio" del paziente)
    acconti.filter(a => a.used === false && a.patientId).forEach(a => {
        if (debtsByPatient[a.patientId]) {
            debtsByPatient[a.patientId].availableCredit += parseFloat(a.amount || 0);
        }
    });

    const list = [];
    let totalUnpaid = 0;
    
    // E. CALCOLO DEBITO NETTO
    for (const pId in debtsByPatient) {
        const data = debtsByPatient[pId];
        const netDebt = data.grossDebt - data.availableCredit;
        if (netDebt > 0) {
            list.push({
                name: data.patientName,
                amount: netDebt,
                gross: data.grossDebt,
                credit: data.availableCredit,
                source: data.details.slice(0, 2).join(' + ') + (data.details.length > 2 ? '...' : '')
            });
            totalUnpaid += netDebt;
        }
    }

    list.sort((a, b) => b.amount - a.amount);
    return { list: list.slice(0, 15), totalUnpaid, count: list.length };
}, [invoices, appointments, purchasedPackages, acconti]);

// 6. Lead time (giorni prenotazione → appuntamento)
const leadTimeStats = useMemo(() => {
  const deltas = [];
  currAppt.forEach(a => {
    const booked  = parseDate(a.createdAt || a.bookedAt);
    const apptDay = a.start;
    if (!booked || !apptDay) return;
    const days = Math.round((apptDay - booked) / (1000 * 60 * 60 * 24));
    if (days >= 0 && days <= 365) deltas.push(days);
  });
  if (!deltas.length) return null;
  deltas.sort((a, b) => a - b);
  const avg    = Math.round(deltas.reduce((s, v) => s + v, 0) / deltas.length);
  const median = deltas[Math.floor(deltas.length / 2)];
  const max    = deltas[deltas.length - 1];
  // distribuzione in bucket
  const buckets = [
    { label: '0–2 gg',   min: 0,  max: 2,   count: 0 },
    { label: '3–7 gg',   min: 3,  max: 7,   count: 0 },
    { label: '8–14 gg',  min: 8,  max: 14,  count: 0 },
    { label: '15–30 gg', min: 15, max: 30,  count: 0 },
    { label: '> 30 gg',  min: 31, max: 999, count: 0 },
  ];
  deltas.forEach(d => {
    const b = buckets.find(b => d >= b.min && d <= b.max);
    if (b) b.count++;
  });
  return { avg, median, max, total: deltas.length, buckets };
}, [currAppt]);

  // ── HR — DERIVED DATA ──────────────────────────────────────────────────────
  const filteredSummaries = useMemo(() => {
    return rawSummaries.filter(s => {
      const staff = staffMap[s.userId];
      if (!staff) return false;
      if (filterRole !== 'all' && staff.role !== filterRole) return false;
      return true;
    });
  }, [rawSummaries, staffMap, filterRole]);

  const financialData = useMemo(() => {
    const monthlyData = {};
    for (let i = startMonth; i <= endMonth; i++) {
      const mStr = moment({ year, month: i }).format('YYYY-MM');
      monthlyData[mStr] = {
        label: moment({ year, month: i }).format('MMM'), month: mStr,
        staffCost: 0, doctorCost: 0, plannedHours: 0, workedHours: 0, overtimeHours: 0, absences: 0,
      };
    }
    filteredSummaries.forEach(summ => {
      if (monthlyData[summ.monthYear]) {
        const cost = calculateStaffCosts(summ, staffMap);
        monthlyData[summ.monthYear].staffCost    += cost;
        monthlyData[summ.monthYear].plannedHours  += summ.totalScheduledHours || 0;
        monthlyData[summ.monthYear].workedHours   += summ.totalWorkedHours    || 0;
        monthlyData[summ.monthYear].overtimeHours += summ.overtimeHours       || 0;
        monthlyData[summ.monthYear].absences      += (summ.sicknessDays || 0) + (summ.leaveDays?.Ferie || 0);
      }
    });
    rawTallies.forEach(tally => { if (monthlyData[tally.monthYear]) monthlyData[tally.monthYear].doctorCost += tally.amount || 0; });
    const chartData   = Object.values(monthlyData);
    const totalStaff  = chartData.reduce((acc, cur) => acc + cur.staffCost,   0);
    const totalDoctors= chartData.reduce((acc, cur) => acc + cur.doctorCost,  0);
    return { chartData, totalStaff, totalDoctors };
  }, [filteredSummaries, rawTallies, staffMap, year, startMonth, endMonth]);

  const roomStatsHR = useMemo(() => {
    if (hrTab !== 'rooms') return null;
    const stats = { roomUsage: {}, equipmentUsage: {} };
    Object.values(roomMap).forEach(r => (stats.roomUsage[r.id] = 0));
    const startD = moment({ year, month: startMonth }).startOf('month');
    const endD   = moment({ year, month: endMonth   }).endOf('month');
    rawAssignments.forEach(assign => {
      let current = moment(assign.validFrom);
      if (current.isBefore(startD)) current = startD.clone();
      const loopEnd = moment(assign.validUntil).isBefore(endD) ? moment(assign.validUntil) : endD;
      const dur = moment(assign.endTime, 'HH:mm').diff(moment(assign.startTime, 'HH:mm'), 'minutes');
      while (current.isSameOrBefore(loopEnd)) {
        if ((assign.daysOfWeek || []).includes(current.isoWeekday())) {
          if (stats.roomUsage[assign.roomId] !== undefined) stats.roomUsage[assign.roomId] += dur;
          const room = roomMap[assign.roomId];
          if (room?.equipmentIds) room.equipmentIds.forEach(eid => { stats.equipmentUsage[eid] = (stats.equipmentUsage[eid] || 0) + dur; });
        }
        current.add(1, 'day');
      }
    });
    const roomChartData = Object.keys(stats.roomUsage).map(rid => ({
      label: roomMap[rid]?.name || rid, minutes: Math.round(stats.roomUsage[rid] / 60),
    })).sort((a, b) => b.minutes - a.minutes);
    const equipmentChartData = Object.keys(stats.equipmentUsage).map(eid => ({
      label: equipmentMap[eid]?.name || eid, minutes: Math.round(stats.equipmentUsage[eid] / 60),
    })).sort((a, b) => b.minutes - a.minutes).slice(0, 10);
    return { roomChartData, equipmentChartData };
  }, [rawAssignments, roomMap, equipmentMap, hrTab, year, startMonth, endMonth]);

  const hrStats = useMemo(() => {
    const userStats = {}; const monthlyData = [];
    for (let i = startMonth; i <= endMonth; i++) {
      const mStr = moment({ year, month: i }).format('YYYY-MM');
      monthlyData.push({ label: moment({ year, month: i }).format('MMM'), month: mStr, plannedHours: 0, workedHours: 0, overtimeHours: 0, absences: 0 });
    }
    filteredSummaries.forEach(summ => {
      if (!userStats[summ.userId]) {
        userStats[summ.userId] = {
          name: summ.userName, plannedHours: 0, workedHours: 0, excessHours: 0,
          sickDays: 0, missedPauses: 0, vacationTaken: 0, lateCount: 0, punctualityRate: 100,
        };
      }
      const stats = userStats[summ.userId];
      stats.plannedHours   += summ.totalScheduledHours || 0;
      stats.workedHours    += summ.totalWorkedHours    || 0;
      stats.excessHours    += summ.excessHours         || 0;
      stats.sickDays       += summ.sicknessDays        || 0;
      stats.missedPauses   += summ.missedPauseHours    || 0;
      stats.vacationTaken  += summ.leaveDays?.Ferie    || 0;
      if (summ.punctualityData) {
        stats.lateCount += summ.punctualityData.lateArrivals || 0;
        const totalShifts = summ.punctualityData.totalShifts || 0;
        if (totalShifts > 0) stats.punctualityRate = ((totalShifts - (summ.punctualityData.lateArrivals || 0)) / totalShifts) * 100;
      }
      const monthData = monthlyData.find(m => m.month === summ.monthYear);
      if (monthData) {
        monthData.plannedHours  += summ.totalScheduledHours || 0;
        monthData.workedHours   += summ.totalWorkedHours    || 0;
        monthData.overtimeHours += summ.overtimeHours       || 0;
        monthData.absences      += (summ.sicknessDays || 0) + (summ.leaveDays?.Ferie || 0);
      }
    });
    const tableData = Object.keys(userStats).map(uid => {
      const st = userStats[uid]; const profile = staffMap[uid];
      return { ...st, vacationBalance: profile?.leaveBalances?.vacationBalance || 0, discrepancy: st.workedHours - st.plannedHours };
    }).sort((a, b) => Math.abs(b.discrepancy) - Math.abs(a.discrepancy));
    const punctualityData   = calculatePunctualityMetrics(filteredSummaries);
    const absenceBreakdown  = {
      sickness:  filteredSummaries.reduce((sum, s) => sum + (s.sicknessDays || 0), 0),
      vacation:  filteredSummaries.reduce((sum, s) => sum + (s.leaveDays?.Ferie || 0), 0),
      permits:   filteredSummaries.reduce((sum, s) => sum + (s.leaveDays?.Permesso || 0), 0),
    };
    return {
      tableData, monthlyData, punctualityData, absenceBreakdown,
      totalPlanned: tableData.reduce((sum, u) => sum + u.plannedHours, 0),
      totalWorked:  tableData.reduce((sum, u) => sum + u.workedHours,  0),
    };
  }, [filteredSummaries, staffMap, year, startMonth, endMonth]);

  const inventoryStats = useMemo(() => {
    if (hrTab !== 'inventory') return null;
    const lowStockProducts = rawInventory
      .filter(p => p.quantity <= (p.reorderThreshold || 0))
      .sort((a, b) => (a.quantity - a.reorderThreshold) - (b.quantity - b.reorderThreshold));
    const totalValue   = rawInventory.reduce((sum, p) => sum + p.quantity * (p.unitCost || 0), 0);
    const consumptionMap = {};
    rawInventoryMovements.forEach(mov => {
      if (mov.type === 'output' || mov.quantity < 0)
        consumptionMap[mov.productId] = (consumptionMap[mov.productId] || 0) + Math.abs(mov.quantity);
    });
    const topConsumed = Object.keys(consumptionMap).map(pid => ({
      label: productMap[pid]?.name || pid,
      quantity: consumptionMap[pid],
      value: consumptionMap[pid] * (productMap[pid]?.unitCost || 0),
    })).sort((a, b) => b.quantity - a.quantity).slice(0, 10);

    const monthlyMovements = {};
    for (let i = startMonth; i <= endMonth; i++) {
      const mStr = moment({ year, month: i }).format('YYYY-MM');
      monthlyMovements[mStr] = { label: moment({ year, month: i }).format('MMM'), Entrate: 0, Uscite: 0 };
    }
    rawInventoryMovements.forEach(mov => {
      const movMonth = moment(mov.date).format('YYYY-MM');
      if (monthlyMovements[movMonth]) {
        if (mov.type === 'input' || mov.quantity > 0) monthlyMovements[movMonth].Entrate += Math.abs(mov.quantity);
        else                                           monthlyMovements[movMonth].Uscite  += Math.abs(mov.quantity);
      }
    });
    const alertProducts = rawInventory.filter(p => (p.lowStockAlerts || 0) >= 2).sort((a, b) => (b.lowStockAlerts || 0) - (a.lowStockAlerts || 0));
    return {
      lowStockProducts, totalValue, topConsumed,
      monthlyChart: Object.values(monthlyMovements),
      alertProducts, totalProducts: rawInventory.length,
    };
  }, [rawInventory, rawInventoryMovements, productMap, hrTab, year, startMonth, endMonth]);

  // ── HR — EVENT HANDLERS ────────────────────────────────────────────────────
  const handleMonthClick   = (d, key)  => setDrillDownModal({ isOpen: true, title: `Dettaglio ${d.label} — ${key}`, data: d, type: 'monthly' });
  const handleRoomClick    = (d)       => setDrillDownModal({ isOpen: true, title: 'Dettaglio Stanza',              data: d, type: 'room'    });
  const handleAbsenceClick = (d)       => setDrillDownModal({ isOpen: true, title: 'Dettaglio Assenze',             data: d, type: 'absence' });
  const handleProductClick = (d)       => setDrillDownModal({ isOpen: true, title: 'Dettaglio Prodotto',            data: d, type: 'product' });

  // ── PERIOD HELPERS (Performance) ──────────────────────────────────────────
  const periodBtns = [
    { v: 'week', l: 'Settimana' }, { v: 'month', l: 'Mese' }, { v: 'lastmonth', l: 'Mese scorso' },
    { v: 'quarter', l: 'Trimestre' }, { v: 'year', l: 'Anno' }, { v: 'custom', l: '📅 Custom' },
  ];
  const periodDesc = { week: 'questa settimana', month: 'mese corrente', lastmonth: 'mese scorso', quarter: 'trimestre corrente', year: 'anno corrente', custom: 'periodo personalizzato' }[period];

  // ╔══════════════════════════════════════════════════════════════════════════╗
  // ║  ↓↓↓  FINE PARTE 1  —  Incolla qui sotto la PARTE 2  ↓↓↓              ║
  // ╚══════════════════════════════════════════════════════════════════════════╝

 // ╔══════════════════════════════════════════════════════════════════════════╗
  // ║  ReportsPage.js  —  PARTE 2 di 2                                        ║
  // ║  Incolla questa parte SOTTO la Parte 1 (sostituisce il commento finale) ║
  // ╚══════════════════════════════════════════════════════════════════════════╝

  // ════════════════════════════════════════════════════════════════════════════
  //  RENDER — PERFORMANCE  (TAB: Report Clienti & Performance)
  // ════════════════════════════════════════════════════════════════════════════

  // ── Dashboard ───────────────────────────────────────────────────────────────
  const renderDashboard = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* 🎯 KPI ROW AGGIORNATA CON I NUOVI VALORI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        <KPICard label={kpis.income.label}     value={kpis.income.value}     sub={kpis.income.sub}     delta={kpis.income.delta}   icon={<Banknote size={21}/>}   color="#059669"/>
        <KPICard label={kpis.appts.label}      value={kpis.appts.value}      sub={kpis.appts.sub}      delta={kpis.appts.delta}    icon={<Calendar size={21}/>}   color="#1d4ed8" isCurrency={false}/>
        <KPICard label={kpis.ticket.label}     value={kpis.ticket.value}     sub={kpis.ticket.sub}     delta={kpis.ticket.delta}   icon={<CreditCard size={21}/>} color="#d97706"/>
        <KPICard label={kpis.uninvoiced.label} value={kpis.uninvoiced.value} sub={kpis.uninvoiced.sub} delta={null}                icon={<Receipt size={21}/>}    color="#7c3aed"/>
        <KPICard label={kpis.lostNoShow.label} value={kpis.lostNoShow.value} sub={kpis.lostNoShow.sub} delta={null}                icon={<UserX size={21}/>}      color="#dc2626"/>
      </div>

      {/* 12-months bar */}
      <Card>
        <SectionTitle>Andamento ultimi 12 mesi</SectionTitle>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={trend12} barGap={2} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
            <Tooltip content={<CustomTooltip/>}/>
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
            <Bar dataKey="Entrate" fill="#1d4ed8" radius={[4, 4, 0, 0]}/>
            <Bar dataKey="Uscite"  fill="#e2e8f0" radius={[4, 4, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
        {/* Payment methods */}
        <Card>
          <SectionTitle>Metodi di pagamento</SectionTitle>
          {payMethods.length === 0
            ? <EmptyState icon={<Wallet size={36}/>} text="Nessun dato nel periodo"/>
            : <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={payMethods} cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="value" paddingAngle={3}>
                      {payMethods.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]}/>)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  {payMethods.map((item, i) => {
                    const tot = payMethods.reduce((s, d) => s + d.value, 0);
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: PALETTE[i % PALETTE.length] }}/>
                          <span style={{ color: '#475569' }}>{item.name}</span>
                        </div>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{tot > 0 ? Math.round(item.value / tot * 100) : 0}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
          }
        </Card>

        {/* Top services */}
        <Card>
          <SectionTitle>Top prestazioni per fatturato</SectionTitle>
          {topServices.length === 0
            ? <EmptyState icon={<Stethoscope size={36}/>} text="Nessuna prestazione nel periodo"/>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {topServices.map((svc, i) => {
                  const max = topServices[0].value;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '130px', fontSize: '0.8rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{svc.name}</div>
                      <div style={{ flex: 1, height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${max > 0 ? (svc.value / max) * 100 : 0}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0f172a', flexShrink: 0 }}>{fmtShort(svc.value)}</div>
                    </div>
                  );
                })}
              </div>
          }
        </Card>
      </div>
    </div>
  );

  // ── Medici ──────────────────────────────────────────────────────────────────
  const renderMedici = () => {
    const active = selectedDoc === 'all' ? doctorStats : doctorStats.filter(d => d.id === selectedDoc);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Doctor filter pills */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button onClick={() => setSelectedDoc('all')} style={{ padding: '7px 16px', borderRadius: '999px', border: '1px solid', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', borderColor: selectedDoc === 'all' ? '#1d4ed8' : '#e2e8f0', background: selectedDoc === 'all' ? '#dbeafe' : 'white', color: selectedDoc === 'all' ? '#1d4ed8' : '#64748b' }}>
            Tutti i medici
          </button>
          {doctors.map(d => (
            <button key={d.id} onClick={() => setSelectedDoc(d.id)} style={{ padding: '7px 16px', borderRadius: '999px', border: '1px solid', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', borderColor: selectedDoc === d.id ? '#1d4ed8' : '#e2e8f0', background: selectedDoc === d.id ? '#dbeafe' : 'white', color: selectedDoc === d.id ? '#1d4ed8' : '#64748b' }}>
              {d.cognome} {d.nome}
            </button>
          ))}
        </div>

        {/* Doctor table */}
        <Card noPad>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Medico', 'Sedute Erogate', 'Fatturato (Cassa)', 'Produzione (Lavoro)', 'Compenso', 'Margine Struttura'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Medico' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {active.map((stat, i) => {
                return (
                  <tr key={stat.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'white' : '#fafafa'}>
                    <td style={{ padding: '14px 16px', fontWeight: '600', color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: PALETTE[i % PALETTE.length] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '800', color: PALETTE[i % PALETTE.length], flexShrink: 0 }}>
                          {stat.name.charAt(0)}
                        </div>
                        {stat.name}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', color: '#475569' }}>{stat.items}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '700', color: '#64748b' }}>{fmt(stat.fat)}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '900', color: '#0f172a' }}>{fmt(stat.prod)}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '700', color: '#7c3aed' }}>{fmt(stat.comp)}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{fmt(stat.strutt)}</td>
                  </tr>
                );
              })}
              {active.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>Nessun dato nel periodo selezionato</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* Comparison bar chart */}
        {active.length > 0 && (
          <Card>
            <SectionTitle>Fatturato · Compenso · Struttura — confronto</SectionTitle>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={active.map(d => ({ name: d.name.split(' ')[0], Produzione: Math.round(d.prod), Compenso: Math.round(d.comp), Struttura: Math.round(d.strutt) }))} barGap={2} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
                <Bar dataKey="Produzione" fill="#1d4ed8" radius={[4, 4, 0, 0]}/>
                <Bar dataKey="Compenso"  fill="#7c3aed" radius={[4, 4, 0, 0]}/>
                <Bar dataKey="Struttura" fill="#059669" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* 6-month trend */}
        {doctors.length > 0 && (
          <Card>
            <SectionTitle>Andamento fatturato per medico — ultimi 6 mesi</SectionTitle>
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={docTrend6}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
                {doctors.map((d, i) => (
                  <Line key={d.id} type="monotone" dataKey={d.cognome || d.nome} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 0 }} activeDot={{ r: 6 }}/>
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Dettaglio singolo medico */}
        {selectedDoc !== 'all' && docServiceBreakdown.length > 0 && (
          <Card>
            <SectionTitle>Prestazioni erogate nel periodo — {doctors.find(d => d.id === selectedDoc)?.cognome}</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {docServiceBreakdown.map((svc, i) => {
                const max = docServiceBreakdown[0].tot;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '160px', flexShrink: 0, fontSize: '0.82rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.name}</div>
                    <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${max > 0 ? (svc.tot / max) * 100 : 0}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                    </div>
                    <div style={{ width: '50px', textAlign: 'right', fontSize: '0.78rem', color: '#94a3b8' }}>{svc.count}x</div>
                    <div style={{ width: '90px', textAlign: 'right', fontWeight: '700', fontSize: '0.82rem', color: '#0f172a' }}>{fmt(svc.tot)}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ── Incassi ─────────────────────────────────────────────────────────────────
  const renderIncassi = () => {
    const exportRows = currInv.map(inv => ({
      Data: inv.date.toLocaleDateString('it-IT'), Tipo: inv.type,
      Importo: parseFloat(inv.amount || 0).toFixed(2), Metodo: inv.method || '',
      'N. Doc': inv.number || '', Paziente: inv.patientName || inv.description || '',
      Medico: inv.doctorName || '', Assicurazione: inv.insuranceName || '',
      Categoria: inv.categoryName || '', Note: inv.notes || '',
    }));
    const balance = incassiStats.income - incassiStats.outcome;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* 4-card summary */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Entrate',               value: incassiStats.income,  color: '#059669', bg: '#f0fdf4', delta: calcDelta(incassiStats.income,  incassiStats.prevInc) },
            { label: 'Uscite',                value: incassiStats.outcome, color: '#dc2626', bg: '#fef2f2', delta: calcDelta(incassiStats.outcome, incassiStats.prevOut) },
            { label: 'Saldo netto',           value: balance,              color: balance >= 0 ? '#1d4ed8' : '#dc2626', bg: '#eff6ff', delta: null },
            { label: 'Crediti assicurativi',  value: incassiStats.pending, color: '#d97706', bg: '#fffbeb', delta: null },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: '150px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20` }}>
              <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '4px', opacity: 0.85 }}>{item.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em', marginBottom: '6px' }}>{fmt(item.value)}</div>
              <DeltaBadge delta={item.delta}/>
            </div>
          ))}
        </div>

        {/* Trend line */}
        <Card>
          <SectionTitle>
            Trend mensile entrate / uscite
            <button onClick={() => exportCSV(exportRows, `report_incassi_${period}.csv`)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>
              <Download size={14}/> Esporta CSV
            </button>
          </SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trend12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
              <Line type="monotone" dataKey="Entrate" stroke="#059669" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }}/>
              <Line type="monotone" dataKey="Uscite"  stroke="#dc2626" strokeWidth={2}   dot={false} strokeDasharray="4 2" activeDot={{ r: 4 }}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
          {/* Payment method breakdown */}
          <Card>
            <SectionTitle>Incassi per metodo di pagamento</SectionTitle>
            {payMethods.length === 0
              ? <EmptyState icon={<Wallet size={32}/>} text="Nessun dato"/>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {payMethods.map((item, i) => {
                    const tot = payMethods.reduce((s, d) => s + d.value, 0);
                    const pct = tot > 0 ? (item.value / tot) * 100 : 0;
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', marginBottom: '5px' }}>
                          <span style={{ color: '#475569' }}>{item.name}</span>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <span style={{ color: '#94a3b8' }}>{Math.round(pct)}%</span>
                            <span style={{ fontWeight: '700', color: '#0f172a' }}>{fmt(item.value)}</span>
                          </div>
                        </div>
                        <div style={{ height: '9px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </Card>

          {/* Category breakdown */}
          <Card>
            <SectionTitle>Movimenti interni per categoria</SectionTitle>
            {catBreakdown.length === 0
              ? <EmptyState icon={<Tag size={32}/>} text="Nessun movimento interno nel periodo"/>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {catBreakdown.map((cat, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tag size={13} color="#94a3b8"/>
                        <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '500' }}>{cat.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '0.82rem' }}>
                        {cat.income  > 0 && <span style={{ color: '#059669', fontWeight: '700' }}>+{fmt(cat.income)}</span>}
                        {cat.outcome > 0 && <span style={{ color: '#dc2626', fontWeight: '700' }}>-{fmt(cat.outcome)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Card>
        </div>

        {/* Top services (incassi view) */}
        {topServices.length > 0 && (
          <Card>
            <SectionTitle>Revenue per tipo di prestazione</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '10px' }}>
              {topServices.map((svc, i) => {
                const tot = topServices.reduce((s, d) => s + d.value, 0);
                const pct = tot > 0 ? Math.round(svc.value / tot * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: PALETTE[i % PALETTE.length] + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE[i % PALETTE.length], flexShrink: 0, fontSize: '0.75rem', fontWeight: '800' }}>{pct}%</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '1px' }}>{fmt(svc.value)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ── Assicurazioni ────────────────────────────────────────────────────────────
  const renderAssicurazioni = () => {
    const totPending    = insuranceStats.reduce((s, i) => s + i.pendingAmt,   0);
    const totFatturato  = insuranceStats.reduce((s, i) => s + i.fatturato,    0);
    const totFranchigie = insuranceStats.reduce((s, i) => s + i.franchigie,   0);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* KPI strip */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Fatturato assicurazioni',   value: totFatturato,  color: '#0891b2', bg: '#ecfeff', icon: <Shield size={26}/> },
            { label: 'Crediti totali in sospeso', value: totPending,    color: '#d97706', bg: '#fffbeb', icon: <AlertTriangle size={26}/> },
            { label: 'Franchigie già incassate',  value: totFranchigie, color: '#059669', bg: '#f0fdf4', icon: <Banknote size={26}/> },
            { label: 'Pratiche aperte', value: insuranceStats.reduce((s, i) => s + i.pendingCount, 0), color: '#7c3aed', bg: '#f5f3ff', icon: <FileText size={26}/>, isCurrency: false },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>
                  {item.isCurrency === false ? (item.value || 0) : fmt(item.value)}
                </div>
              </div>
              <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
            </div>
          ))}
        </div>

        {/* Per-period chart */}
        {insPeriodStats.length > 0 && (
          <Card>
            <SectionTitle>Fatturato assicurativo nel periodo — confronto</SectionTitle>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={insPeriodStats.map(i => ({ name: i.name, Fatturato: Math.round(i.fatturato), Sospeso: Math.round(i.pending), Franchigie: Math.round(i.franchigie) }))} barGap={2} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
                <Bar dataKey="Fatturato"  fill="#0891b2" radius={[4, 4, 0, 0]}/>
                <Bar dataKey="Sospeso"    fill="#f59e0b" radius={[4, 4, 0, 0]}/>
                <Bar dataKey="Franchigie" fill="#059669" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Insurance table */}
        <Card noPad>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Assicurazione', 'Fatturato', 'Franchigie', 'Sospeso', 'Pratiche', 'Stato'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Assicurazione' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {insuranceStats.map((ins, i) => (
                <tr key={ins.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '13px 16px', fontWeight: '600', color: '#0f172a' }}>{ins.name}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: '700' }}>{fmt(ins.fatturato)}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', color: '#059669', fontWeight: '600' }}>{fmt(ins.franchigie)}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', color: ins.pendingAmt > 0 ? '#d97706' : '#059669', fontWeight: '700' }}>{fmt(ins.pendingAmt)}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', color: '#475569' }}>{ins.pendingCount} pratiche</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                    <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '700', background: ins.pendingAmt === 0 ? '#dcfce7' : '#fef3c7', color: ins.pendingAmt === 0 ? '#166534' : '#92400e' }}>
                      {ins.pendingAmt === 0 ? '✓ In pari' : '⏳ Sospeso'}
                    </span>
                  </td>
                </tr>
              ))}
              {insuranceStats.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>Nessun movimento assicurativo registrato</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* Oldest pending */}
        {oldestPending.length > 0 && (
          <Card>
            <SectionTitle>
              Pratiche sospese più vecchie — priorità di sollecito
              <button onClick={() => exportCSV(oldestPending.map(i => ({ Paziente: i.patientName || '', Assicurazione: i.insuranceName || '', Importo: parseFloat(i.amount || 0).toFixed(2), Franchigia: parseFloat(i.franchigia || 0).toFixed(2), Sospeso: (parseFloat(i.amount || 0) - parseFloat(i.franchigia || 0)).toFixed(2), Data: i.date.toLocaleDateString('it-IT'), GiorniApertura: i.daysAgo })), 'pratiche_sospese.csv')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>
                <Download size={14}/> Esporta
              </button>
            </SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {oldestPending.map((inv, i) => {
                const sospeso = parseFloat(inv.amount || 0) - parseFloat(inv.franchigia || 0);
                const urgency = inv.daysAgo > 90 ? '#dc2626' : inv.daysAgo > 60 ? '#d97706' : '#f59e0b';
                const urgBg   = inv.daysAgo > 90 ? '#fef2f2' : inv.daysAgo > 60 ? '#fffbeb' : '#fefce8';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                    <div style={{ padding: '4px 10px', borderRadius: '999px', background: urgBg, color: urgency, fontSize: '0.75rem', fontWeight: '700', flexShrink: 0, minWidth: '70px', textAlign: 'center' }}>{inv.daysAgo}gg</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.patientName || 'N/D'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{inv.insuranceName || 'Assicurazione'} · {inv.date.toLocaleDateString('it-IT')}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: urgency }}>{fmt(sospeso)}</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Doc #{inv.number}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ── Pazienti ─────────────────────────────────────────────────────────────────
  const renderPazienti = () => {
    const activeCount   = patientActivity.find(p => p.name === 'Attivi')?.value         || 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Pazienti in archivio',        value: patients.length,       color: '#0891b2', bg: '#ecfeff', icon: <Users size={26}/>,       isCurrency: false },
            { label: 'Nuovi nel periodo',            value: newPatientsInPeriod,   color: '#059669', bg: '#f0fdf4', icon: <UserCheck size={26}/>,    isCurrency: false },
            { label: 'Attivi (ultimi 90gg)',         value: activeCount,           color: '#1d4ed8', bg: '#eff6ff', icon: <Activity size={26}/>,     isCurrency: false },
            { label: 'Media appuntamenti/paziente',  value: avgApptsPerPatient,    color: '#d97706', bg: '#fffbeb', icon: <CalendarDays size={26}/>, isCurrency: false, decimals: 1 },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>
                  {item.decimals ? (item.value || 0).toFixed(item.decimals) : (item.value || 0).toLocaleString('it-IT')}
                </div>
              </div>
              <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
            </div>
          ))}
        </div>

        <Card>
          <SectionTitle>Nuovi pazienti per mese — ultimi 12 mesi</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={newPatientsByMonth} barCategoryGap="40%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Bar dataKey="Nuovi" fill="#0891b2" radius={[6, 6, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
          {/* Activity breakdown */}
          <Card>
            <SectionTitle>Stato attività pazienti</SectionTitle>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={patientActivity.filter(p => p.value > 0)} cx="50%" cy="50%" innerRadius={42} outerRadius={68} dataKey="value" paddingAngle={3}>
                  {patientActivity.map((p, i) => <Cell key={i} fill={p.color}/>)}
                </Pie>
                <Tooltip formatter={v => [v, 'Pazienti']}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '4px' }}>
              {patientActivity.filter(p => p.value > 0).map((p, i) => {
                const tot = patientActivity.reduce((s, x) => s + x.value, 0);
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: p.color, flexShrink: 0 }}/>
                      <span style={{ color: '#475569' }}>{p.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span style={{ color: '#94a3b8' }}>{tot > 0 ? Math.round(p.value / tot * 100) : 0}%</span>
                      <span style={{ fontWeight: '700', color: '#0f172a', minWidth: '40px', textAlign: 'right' }}>{p.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Gender breakdown */}
          <Card>
            <SectionTitle>Distribuzione per sesso</SectionTitle>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={patientGender.filter(p => p.value > 0)} cx="50%" cy="50%" innerRadius={42} outerRadius={68} dataKey="value" paddingAngle={3}>
                  {patientGender.map((p, i) => <Cell key={i} fill={p.color}/>)}
                </Pie>
                <Tooltip formatter={v => [v, 'Pazienti']}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '4px' }}>
              {patientGender.filter(p => p.value > 0).map((p, i) => {
                const tot = patientGender.reduce((s, x) => s + x.value, 0);
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: p.color, flexShrink: 0 }}/>
                      <span style={{ color: '#475569' }}>{p.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span style={{ color: '#94a3b8' }}>{tot > 0 ? Math.round(p.value / tot * 100) : 0}%</span>
                      <span style={{ fontWeight: '700', color: '#0f172a', minWidth: '40px', textAlign: 'right' }}>{p.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Expiring packages */}
        {expiringPackages.length > 0 && (
          <Card>
            <SectionTitle>⚠️ Pacchetti in scadenza (prossimi 30 giorni)</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '10px' }}>
              {expiringPackages.map((pkg, i) => {
                const daysLeft = pkg.endDateParsed ? Math.ceil((pkg.endDateParsed - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const urgency  = daysLeft !== null ? (daysLeft <= 7 ? '#dc2626' : daysLeft <= 14 ? '#d97706' : '#f59e0b') : (pkg.sessionsLeft <= 1 ? '#dc2626' : '#f59e0b');
                const urgBg    = daysLeft !== null ? (daysLeft <= 7 ? '#fef2f2' : daysLeft <= 14 ? '#fffbeb' : '#fefce8') : (pkg.sessionsLeft <= 1 ? '#fef2f2' : '#fefce8');
                return (
                  <div key={i} style={{ padding: '12px 14px', background: urgBg, borderRadius: '12px', border: `1px solid ${urgency}30` }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg.patientName || 'Paziente N/D'}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px' }}>{pkg.serviceName || 'Pacchetto'}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {daysLeft !== null && <span style={{ padding: '2px 10px', borderRadius: '999px', background: urgency + '20', color: urgency, fontSize: '0.75rem', fontWeight: '700' }}><Timer size={10} style={{ display: 'inline', marginRight: '3px' }}/>{daysLeft}gg rimasti</span>}
                      {pkg.sessionsLeft !== undefined && <span style={{ padding: '2px 10px', borderRadius: '999px', background: '#ede9fe', color: '#6d28d9', fontSize: '0.75rem', fontWeight: '700' }}>{pkg.sessionsLeft} sedute rimaste</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Top patients */}
        {topPatients.length > 0 && (
          <Card>
            <SectionTitle>Top 8 pazienti per fatturato — {periodDesc}</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {topPatients.map((p, i) => {
                const max = topPatients[0].fat;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderBottom: i < topPatients.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ width: '28px', textAlign: 'center', flexShrink: 0, fontSize: '0.78rem', fontWeight: '800', color: i < 3 ? PALETTE[i] : '#cbd5e1' }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0f172a', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      <div style={{ height: '5px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${max > 0 ? (p.fat / max) * 100 : 0}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a' }}>{fmt(p.fat)}</div>
                      <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>{p.n} fatture</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ── Agenda ───────────────────────────────────────────────────────────────────
  const renderAgenda = () => {
    const DAYS    = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
    const heatMax = Math.max(...heatmapData.flat.map(c => c.value), 1);
    const getHeatColor = (v) => {
      if (v === 0) return '#f1f5f9';
      const intensity = Math.min(v / heatMax, 1);
      if (intensity < 0.33) return '#bfdbfe';
      if (intensity < 0.66) return '#3b82f6';
      return '#1d4ed8';
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* KPI strip */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Appuntamenti nel periodo',  value: currAppt.length, color: '#1d4ed8', bg: '#eff6ff', icon: <Calendar size={26}/>,    isCurrency: false },
            { label: 'Stanze attive',             value: rooms.length,    color: '#0891b2', bg: '#ecfeff', icon: <BedDouble size={26}/>,   isCurrency: false },
            { label: 'Ore erogate (stima)',        value: Math.round(currAppt.reduce((s, a) => { const d = a.end && a.start ? (a.end - a.start) / (1000 * 60 * 60) : 0; return s + d; }, 0) * 10) / 10, color: '#059669', bg: '#f0fdf4', icon: <Clock size={26}/>, isCurrency: false, unit: 'h' },
            { label: 'Media app/giorno lavorativo', value: Math.round(currAppt.length / 22 * 10) / 10, color: '#d97706', bg: '#fffbeb', icon: <TrendingUp size={26}/>, isCurrency: false, decimals: 1 },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>
                  {item.decimals ? (item.value || 0).toFixed(item.decimals) : (item.value || 0).toLocaleString('it-IT')}{item.unit || ''}
                </div>
              </div>
              <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
            </div>
          ))}
        </div>

        {/* Heatmap */}
        <Card>
          <SectionTitle>Mappa di calore — fasce orarie (dati storici)</SectionTitle>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: '3px', minWidth: '500px' }}>
              <thead>
                <tr>
                  <td style={{ width: '40px' }}/>
                  {DAYS.map(d => (
                    <td key={d} style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', padding: '0 2px', minWidth: '44px' }}>{d}</td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 14 }, (_, hi) => {
                  const h = 7 + hi;
                  return (
                    <tr key={h}>
                      <td style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'right', paddingRight: '8px', verticalAlign: 'middle' }}>{h}:00</td>
                      {DAYS.map(day => {
                        const v = heatmapData.grid[day]?.[h] || 0;
                        return (
                          <td key={day} title={`${day} ${h}:00 — ${v} appuntamenti`}
                            style={{ width: '44px', height: '28px', borderRadius: '6px', background: getHeatColor(v), cursor: 'default', transition: 'transform 0.1s', textAlign: 'center', fontSize: '0.68rem', fontWeight: v > 0 ? '700' : '400', color: v > 0 ? 'white' : 'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                            {v || ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '0.73rem', color: '#94a3b8' }}>
              <span>Meno affollato</span>
              {['#f1f5f9', '#bfdbfe', '#3b82f6', '#1d4ed8'].map((c, i) => (
                <div key={i} style={{ width: '18px', height: '18px', borderRadius: '4px', background: c }}/>
              ))}
              <span>Più affollato</span>
            </div>
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
          {/* Room occupancy */}
          <Card>
            <SectionTitle>Utilizzo stanze nel periodo</SectionTitle>
            {roomOccupancy.length === 0
              ? <EmptyState icon={<BedDouble size={32}/>} text="Nessuna stanza configurata"/>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                  {roomOccupancy.map((r, i) => (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', marginBottom: '5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <BedDouble size={13} color={PALETTE[i % PALETTE.length]}/>
                          <span style={{ color: '#475569', fontWeight: '500' }}>{r.name}</span>
                        </div>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{r.total} app.</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${r.pct}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Card>

          {/* Appuntamenti per giorno settimana */}
          <Card>
            <SectionTitle>Distribuzione per giorno della settimana</SectionTitle>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={apptsByWeekday} barCategoryGap="40%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="giorno" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Bar dataKey="Appuntamenti" fill="#0891b2" radius={[5, 5, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Hours per doctor */}
        {hoursPerDoctor.length > 0 && (
          <Card>
            <SectionTitle>Ore lavorate per medico nel periodo (stima da durata appuntamenti)</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {hoursPerDoctor.map((d, i) => {
                const max = hoursPerDoctor[0].hours;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: PALETTE[i % PALETTE.length] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: '800', color: PALETTE[i % PALETTE.length], flexShrink: 0 }}>{d.name.charAt(0)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.83rem', fontWeight: '600', color: '#0f172a', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                      <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${max > 0 ? (d.hours / max) * 100 : 0}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a' }}>{d.hours}h</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{d.appts} app.</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Busiest days */}
        {busyDays.length > 0 && (
          <Card>
            <SectionTitle>Giorni più intensi nel periodo</SectionTitle>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {busyDays.map((d, i) => (
                <div key={i} style={{ flex: 1, minWidth: '130px', padding: '14px 16px', background: i === 0 ? '#eff6ff' : '#f8fafc', borderRadius: '12px', border: i === 0 ? '1px solid #bfdbfe' : '1px solid #f1f5f9', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: i === 0 ? '#1d4ed8' : '#0f172a', letterSpacing: '-0.02em' }}>{d.count}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{d.date}</div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ── Metriche Avanzate ──────────────────────────────────────────────────────
const renderAvanzate = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

    {/* ── RIGA 1: No-Show + Lead Time KPI ── */}
    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
      {noShowStats && [
        { label: 'Tasso No-Show',     value: `${noShowStats.noShowRate}%`,  color: '#dc2626', bg: '#fef2f2', icon: <UserX size={26}/> },
        { label: 'Salvati da WA',     value: `${noShowStats.saveRate}%`,    color: '#059669', bg: '#f0fdf4', icon: <UserCheck size={26}/> },
        { label: 'In attesa risposta',value: noShowStats.pending,           color: '#d97706', bg: '#fffbeb', icon: <Clock size={26}/>, raw: true },
      ].map((item, i) => (
        <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>{item.raw ? item.value : item.value}</div>
          </div>
          <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
        </div>
      ))}
      {leadTimeStats && [
        { label: 'Lead time medio',   value: `${leadTimeStats.avg} gg`,    color: '#1d4ed8', bg: '#eff6ff', icon: <Timer size={26}/> },
        { label: 'Lead time mediana', value: `${leadTimeStats.median} gg`, color: '#7c3aed', bg: '#f5f3ff', icon: <CalendarDays size={26}/> },
      ].map((item, i) => (
        <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>{item.value}</div>
          </div>
          <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
        </div>
      ))}
    </div>

    {/* ── RIGA 2: No-Show donut + Lead Time distribution ── */}
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>

      {/* No-Show donut */}
      <Card>
        <SectionTitle>Esito appuntamenti — efficacia promemoria WA</SectionTitle>
        {!noShowStats
          ? <EmptyState icon={<UserX size={32}/>} text="Nessun dato nel periodo"/>
          : <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={noShowStats.chartData} cx="50%" cy="50%" innerRadius={48} outerRadius={75} dataKey="value" paddingAngle={3}>
                    {noShowStats.chartData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                  </Pie>
                  <Tooltip formatter={v => [v, 'appuntamenti']}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '4px' }}>
                {noShowStats.chartData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: d.color, flexShrink: 0 }}/>
                      <span style={{ color: '#475569' }}>{d.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span style={{ color: '#94a3b8' }}>{Math.round(d.value / noShowStats.total * 100)}%</span>
                      <span style={{ fontWeight: '700', color: '#0f172a', minWidth: '32px', textAlign: 'right' }}>{d.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
        }
      </Card>

      {/* Lead time distribution */}
      <Card>
        <SectionTitle>Distribuzione lead time prenotazione → visita</SectionTitle>
        {!leadTimeStats
          ? <EmptyState icon={<Timer size={32}/>} text="Dati createdAt non disponibili sugli appuntamenti"/>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '13px', marginTop: '4px' }}>
              {leadTimeStats.buckets.map((b, i) => {
                const max = Math.max(...leadTimeStats.buckets.map(x => x.count), 1);
                const pct = Math.round((b.count / leadTimeStats.total) * 100);
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: '5px' }}>
                      <span style={{ color: '#475569', fontWeight: '500' }}>{b.label}</span>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <span style={{ color: '#94a3b8' }}>{pct}%</span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{b.count} app.</span>
                      </div>
                    </div>
                    <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(b.count / max) * 100}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                    </div>
                  </div>
                );
              })}
              <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 14px', fontSize: '0.82rem', color: '#475569', marginTop: '4px' }}>
                📊 Media <strong style={{ color: '#1d4ed8' }}>{leadTimeStats.avg} giorni</strong> · Mediana <strong style={{ color: '#7c3aed' }}>{leadTimeStats.median} giorni</strong> · Max <strong style={{ color: '#dc2626' }}>{leadTimeStats.max} giorni</strong>
              </div>
            </div>
        }
      </Card>
    </div>

    {/* ── RIGA 3: Room Revenue + Packages vs Single ── */}
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>

      {/* Room saturation & revenue */}
      <Card>
        <SectionTitle>Redditività & saturazione stanze</SectionTitle>
        {roomRevenueStats.length === 0
          ? <EmptyState icon={<BedDouble size={32}/>} text="Collega roomId alle fatture per questo dato"/>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {roomRevenueStats.map((r, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <BedDouble size={13} color={PALETTE[i % PALETTE.length]}/>
                      <span style={{ fontSize: '0.84rem', fontWeight: '600', color: '#0f172a' }}>{r.name}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#059669' }}>
                        {r.revenuePerH > 0 ? `€ ${r.revenuePerH}/h` : '—'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.appts} app. · {r.saturation}% sat.</div>
                    </div>
                  </div>
                  {/* saturazione */}
                  <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden', marginBottom: '3px' }}>
                    <div style={{ height: '100%', width: `${r.saturation}%`, background: r.saturation > 70 ? '#059669' : r.saturation > 40 ? '#d97706' : '#dc2626', borderRadius: '99px' }}/>
                  </div>
                </div>
              ))}
            </div>
        }
      </Card>

      {/* Packages vs Single */}
      <Card>
        <SectionTitle>Pacchetti vs singole sedute</SectionTitle>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          {[
            { label: 'Revenue pacchetti',  value: fmt(packageVsSingle.pkgRevenue),    color: '#1d4ed8', pct: packageVsSingle.pkgPct,    count: packageVsSingle.pkgCount    },
            { label: 'Revenue singole',    value: fmt(packageVsSingle.singleRevenue), color: '#0891b2', pct: packageVsSingle.singlePct, count: packageVsSingle.singleCount },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, background: '#f8fafc', borderRadius: '12px', padding: '12px 14px', borderLeft: `3px solid ${item.color}` }}>
              <div style={{ fontSize: '0.75rem', color: item.color, fontWeight: '600', marginBottom: '3px' }}>{item.label}</div>
              <div style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>{item.value}</div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>{item.pct}% del totale · {item.count} vendite</div>
            </div>
          ))}
        </div>
        {packageVsSingle.chartData.length > 0
          ? <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={packageVsSingle.chartData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={4}>
                  {packageVsSingle.chartData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Tooltip formatter={v => [`€ ${v.toLocaleString('it-IT')}`, '']}/>
              </PieChart>
            </ResponsiveContainer>
          : <EmptyState icon={<Package size={28}/>} text="Nessun dato nel periodo"/>
        }
      </Card>
    </div>

    {/* ── RIGA 4: Churn / LTV + Insoluti ── */}
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>

      {/* Churn & LTV */}
      <Card>
        <SectionTitle>Fidelizzazione pazienti & Life-Time Value</SectionTitle>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'LTV medio paziente', value: fmt(churnStats.ltv),          color: '#059669' },
            { label: 'Tasso abbandono',    value: `${churnStats.churnRate}%`,   color: '#dc2626' },
            { label: 'Fidelizzati (5+)',   value: churnStats.loyal,             color: '#1d4ed8', raw: true },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center', background: '#f8fafc', borderRadius: '12px', padding: '12px 8px' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: '700', color: item.color }}>{item.raw ? item.value : item.value}</div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>{item.label}</div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={130}>
          <PieChart>
            <Pie data={churnStats.chartData} cx="50%" cy="50%" innerRadius={36} outerRadius={60} dataKey="value" paddingAngle={3}>
              {churnStats.chartData.map((d, i) => <Cell key={i} fill={d.color}/>)}
            </Pie>
            <Tooltip formatter={v => [v, 'pazienti']}/>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
          {churnStats.chartData.map((d, i) => {
            const pct = churnStats.total > 0 ? Math.round(d.value / churnStats.total * 100) : 0;
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.81rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.color }}/>
                  <span style={{ color: '#475569' }}>{d.name}</span>
                </div>
                <span style={{ fontWeight: '700', color: '#0f172a' }}>{d.value} <span style={{ color: '#94a3b8', fontWeight: '400' }}>({pct}%)</span></span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Insoluti / crediti da recuperare */}
      <Card style={{ border: unpaidStats.totalUnpaid > 0 ? '1px solid #fee2e2' : '1px solid #f1f5f9' }}>
        <SectionTitle>
          🔴 Crediti da recuperare
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#dc2626' }}>
            {fmt(unpaidStats.totalUnpaid)}
          </span>
        </SectionTitle>
        {unpaidStats.list.length === 0
          ? <EmptyState icon={<Receipt size={32}/>} text="Nessun insoluto rilevato ✅"/>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {unpaidStats.list.map((item, i) => {
                const urgency = item.daysAgo > 90 ? '#dc2626' : item.daysAgo > 30 ? '#d97706' : '#f59e0b';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                    <div style={{ padding: '3px 8px', borderRadius: '999px', background: urgency + '20', color: urgency, fontSize: '0.72rem', fontWeight: '700', flexShrink: 0, minWidth: '55px', textAlign: 'center' }}>
                      {item.daysAgo}gg
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.83rem', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{item.source}</div>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#dc2626', flexShrink: 0 }}>{fmt(item.amount)}</div>
                  </div>
                );
              })}
              {unpaidStats.count > 12 && (
                <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8', paddingTop: '4px' }}>
                  + altri {unpaidStats.count - 12} crediti (vai alla tab Assicurazioni per il dettaglio completo)
                </div>
              )}
            </div>
        }
      </Card>
    </div>

  </div>
);

  // ════════════════════════════════════════════════════════════════════════════
  //  RENDER — HR  (TAB: Report HR & Inventario)
  // ════════════════════════════════════════════════════════════════════════════

  // ── HR: Personale ────────────────────────────────────────────────────────────
  const renderHRPersonale = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* KPI strip */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Ore Pianificate', value: hrStats.totalPlanned.toFixed(0) + ' h', color: '#1d4ed8', bg: '#eff6ff', icon: <Calendar size={26}/> },
          { label: 'Ore Lavorate',    value: hrStats.totalWorked.toFixed(0)  + ' h', color: '#059669', bg: '#f0fdf4', icon: <Clock size={26}/> },
          { label: 'Discrepanza',     value: (hrStats.totalWorked - hrStats.totalPlanned >= 0 ? '+' : '') + (hrStats.totalWorked - hrStats.totalPlanned).toFixed(0) + ' h', color: Math.abs(hrStats.totalWorked - hrStats.totalPlanned) > 20 ? '#dc2626' : '#d97706', bg: '#fffbeb', icon: <TrendingUp size={26}/> },
          { label: 'Assenze Malattia', value: hrStats.absenceBreakdown.sickness + ' gg', color: '#7c3aed', bg: '#f5f3ff', icon: <AlertTriangle size={26}/> },
        ].map((item, i) => (
          <div key={i} style={{ flex: 1, minWidth: '155px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>{item.value}</div>
            </div>
            <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
          </div>
        ))}
      </div>

      {/* Pianificato vs Lavorato */}
      <Card>
        <SectionTitle>Pianificato vs Lavorato — andamento mensile</SectionTitle>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={hrStats.monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}h`}/>
            <Tooltip content={<CustomTooltip/>}/>
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
            <Line type="monotone" dataKey="plannedHours" name="Pianificate" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3, strokeWidth: 0 }} activeDot={{ r: 6 }} strokeDasharray="4 2"/>
            <Line type="monotone" dataKey="workedHours"  name="Lavorate"    stroke="#1d4ed8" strokeWidth={2.5} dot={{ r: 3, strokeWidth: 0 }} activeDot={{ r: 6 }}/>
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
        {/* Assenze per tipo */}
        <Card>
          <SectionTitle>Analisi assenze per tipo</SectionTitle>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Malattia', value: hrStats.absenceBreakdown.sickness },
                  { name: 'Ferie',    value: hrStats.absenceBreakdown.vacation },
                  { name: 'Permessi', value: hrStats.absenceBreakdown.permits  },
                ].filter(d => d.value > 0)}
                cx="50%" cy="50%" innerRadius={42} outerRadius={68} dataKey="value" paddingAngle={3}
                onClick={(entry) => handleAbsenceClick(entry)}
                style={{ cursor: 'pointer' }}
              >
                {['#dc2626', '#0891b2', '#f59e0b'].map((c, i) => <Cell key={i} fill={c}/>)}
              </Pie>
              <Tooltip formatter={v => [v, 'giorni']}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
            {[
              { name: 'Malattia', value: hrStats.absenceBreakdown.sickness, color: '#dc2626' },
              { name: 'Ferie',    value: hrStats.absenceBreakdown.vacation,  color: '#0891b2' },
              { name: 'Permessi', value: hrStats.absenceBreakdown.permits,   color: '#f59e0b' },
            ].map((item, i) => {
              const tot = hrStats.absenceBreakdown.sickness + hrStats.absenceBreakdown.vacation + hrStats.absenceBreakdown.permits || 1;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: item.color, flexShrink: 0 }}/>
                    <span style={{ color: '#475569' }}>{item.name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <span style={{ color: '#94a3b8' }}>{Math.round(item.value / tot * 100)}%</span>
                    <span style={{ fontWeight: '700', color: '#0f172a', minWidth: '40px', textAlign: 'right' }}>{item.value} gg</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Top 5 puntualità */}
        <Card>
          <SectionTitle>Indice puntualità dipendenti</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {hrStats.punctualityData
              .sort((a, b) => a.punctualityRate - b.punctualityRate)
              .slice(0, 6)
              .map((user, i) => {
                const col = user.punctualityRate >= 90 ? '#059669' : user.punctualityRate >= 75 ? '#d97706' : '#dc2626';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: col + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800', color: col, flexShrink: 0 }}>
                      {user.name.charAt(0)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#0f172a', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                      <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${user.punctualityRate}%`, background: col, borderRadius: '99px' }}/>
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '700', color: col }}>{user.punctualityRate.toFixed(0)}%</div>
                      {user.lateArrivals > 0 && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{user.lateArrivals} ritardi</div>}
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      </div>

      {/* Tabella dettagliata */}
      <Card noPad>
        <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Analisi dettagliata performance e assenze</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Dipendente','Ore Pian.','Ore Lav.','Discrepanza','Malattia','Pause Mancate','Ferie Godute','Saldo Ferie'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: h === 'Dipendente' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hrStats.tableData.map((user, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'white' : '#fafafa'}>
                  <td style={{ padding: '13px 14px', fontWeight: '600', color: '#0f172a' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: PALETTE[i % PALETTE.length] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '800', color: PALETTE[i % PALETTE.length], flexShrink: 0 }}>
                        {user.name.charAt(0)}
                      </div>
                      {user.name}
                    </div>
                  </td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', color: '#475569' }}>{user.plannedHours.toFixed(1)} h</td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', color: '#475569' }}>{user.workedHours.toFixed(1)} h</td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', fontWeight: '700', color: user.discrepancy > 10 ? '#dc2626' : user.discrepancy < -10 ? '#d97706' : '#059669' }}>
                    {user.discrepancy >= 0 ? '+' : ''}{user.discrepancy.toFixed(1)} h
                  </td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', color: user.sickDays > 5 ? '#dc2626' : '#475569' }}>{user.sickDays} gg</td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', color: '#475569' }}>{user.missedPauses.toFixed(1)} h</td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', color: '#475569' }}>{user.vacationTaken} gg</td>
                  <td style={{ padding: '13px 14px', textAlign: 'right', fontWeight: '700', color: '#1d4ed8' }}>{user.vacationBalance.toFixed(2)} gg</td>
                </tr>
              ))}
              {hrStats.tableData.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>Nessun dato nel periodo selezionato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  // ── HR: Costi & Finanza ───────────────────────────────────────────────────────
  const renderHRFinanza = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Costo totale dipendenti', value: fmt(financialData.totalStaff),   color: '#1d4ed8', bg: '#eff6ff', icon: <Users size={26}/>,    sub: 'Lordi + contributi + rimborsi' },
          { label: 'Costo totale medici',     value: fmt(financialData.totalDoctors), color: '#059669', bg: '#f0fdf4', icon: <Briefcase size={26}/>, sub: 'Fatture P.IVA approvate' },
          { label: 'Costo totale periodo',    value: fmt(financialData.totalStaff + financialData.totalDoctors), color: '#7c3aed', bg: '#f5f3ff', icon: <Wallet size={26}/>, sub: 'Staff + medici' },
        ].map((item, i) => (
          <div key={i} style={{ flex: 1, minWidth: '180px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
              <div style={{ fontSize: '1.45rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>{item.value}</div>
              <div style={{ fontSize: '0.72rem', color: item.color, opacity: 0.6, marginTop: '2px' }}>{item.sub}</div>
            </div>
            <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
          </div>
        ))}
      </div>

      <Card>
        <SectionTitle>Andamento costi mensili — Dipendenti vs Medici</SectionTitle>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={financialData.chartData} barGap={4} barCategoryGap="35%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}/>
            <Tooltip content={<CustomTooltip/>}/>
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
            <Bar dataKey="staffCost"   name="Dipendenti" fill="#1d4ed8" radius={[4, 4, 0, 0]} onClick={(d) => handleMonthClick(d, 'staffCost')} style={{ cursor: 'pointer' }}/>
            <Bar dataKey="doctorCost"  name="Medici"     fill="#059669" radius={[4, 4, 0, 0]} onClick={(d) => handleMonthClick(d, 'doctorCost')} style={{ cursor: 'pointer' }}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );

  // ── HR: Stanze ────────────────────────────────────────────────────────────────
  const renderHRStanze = () => {
    if (!roomStatsHR) return <EmptyState icon={<BedDouble size={36}/>} text="Caricamento dati stanze…"/>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <Card>
          <SectionTitle>Ore di occupazione per stanza</SectionTitle>
          {roomStatsHR.roomChartData.length === 0
            ? <EmptyState icon={<BedDouble size={32}/>} text="Nessuna assegnazione stanza nel periodo"/>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {roomStatsHR.roomChartData.map((r, i) => {
                  const max = roomStatsHR.roomChartData[0].minutes || 1;
                  return (
                    <div key={i} onClick={() => handleRoomClick(r)} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', marginBottom: '5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <BedDouble size={13} color={PALETTE[i % PALETTE.length]}/>
                          <span style={{ color: '#475569', fontWeight: '500' }}>{r.label}</span>
                        </div>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{r.minutes} h</span>
                      </div>
                      <div style={{ height: '9px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(r.minutes / max) * 100}%`, background: PALETTE[i % PALETTE.length], borderRadius: '99px' }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </Card>

        {roomStatsHR.equipmentChartData.length > 0 && (
          <Card noPad>
            <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>Top 10 macchinari utilizzati</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {['Macchinario', 'Ore Utilizzo', 'Intensità'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Macchinario' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roomStatsHR.equipmentChartData.map((eq, i) => {
                  const max = roomStatsHR.equipmentChartData[0].minutes || 1;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '13px 16px', fontWeight: '600', color: '#0f172a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                          <Wrench size={14} color={PALETTE[i % PALETTE.length]}/>
                          {eq.label}
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>{eq.minutes} h</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                          <div style={{ width: '80px', height: '7px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(eq.minutes / max) * 100}%`, background: '#d97706', borderRadius: '99px' }}/>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    );
  };

  // ── HR: Magazzino ─────────────────────────────────────────────────────────────
  const renderHRInventario = () => {
    if (!inventoryStats) return <EmptyState icon={<Package size={36}/>} text="Caricamento magazzino…"/>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* KPI strip */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Valore totale magazzino',   value: fmt(inventoryStats.totalValue),             color: '#7c3aed', bg: '#f5f3ff', icon: <Package size={26}/> },
            { label: 'Prodotti sotto soglia',      value: `${inventoryStats.lowStockProducts.length} / ${inventoryStats.totalProducts}`, color: '#dc2626', bg: '#fef2f2', icon: <AlertTriangle size={26}/>, raw: true },
            { label: 'Prodotti critici (2+ alert)', value: `${inventoryStats.alertProducts.length}`,  color: '#d97706', bg: '#fffbeb', icon: <TrendingDown size={26}/>, raw: true },
          ].map((item, i) => (
            <div key={i} style={{ flex: 1, minWidth: '180px', background: item.bg, borderRadius: '16px', padding: '1.25rem 1.5rem', border: `1px solid ${item.color}20`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: item.color, fontWeight: '600', marginBottom: '3px', opacity: 0.85 }}>{item.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: item.color, letterSpacing: '-0.02em' }}>{item.value}</div>
              </div>
              <div style={{ opacity: 0.25, color: item.color }}>{item.icon}</div>
            </div>
          ))}
        </div>

        {/* Movimenti mensili */}
        <Card>
          <SectionTitle>Movimenti magazzino mensili — Entrate vs Uscite</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={inventoryStats.monthlyChart} barGap={4} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }}/>
              <Bar dataKey="Entrate" fill="#059669" radius={[4, 4, 0, 0]}/>
              <Bar dataKey="Uscite"  fill="#dc2626" radius={[4, 4, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1.5rem' }}>
          {/* Top 10 consumati */}
          <Card noPad>
            <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>🔥 Top 10 prodotti più consumati</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Prodotto', 'Qtà', 'Valore'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Prodotto' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inventoryStats.topConsumed.map((prod, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8f8f8', cursor: 'pointer', transition: 'background 0.1s' }}
                    onClick={() => handleProductClick(prod)}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '11px 14px', fontWeight: '600', color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: PALETTE[i % PALETTE.length] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '800', color: PALETTE[i % PALETTE.length], flexShrink: 0 }}>{i + 1}</div>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prod.label}</span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', color: '#475569' }}>{prod.quantity}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '700', color: '#d97706' }}>€ {prod.value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Alert prodotti critici */}
          <Card>
            <SectionTitle>🚨 Prodotti critici (alert ≥ 2)</SectionTitle>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '14px' }}>Prodotti finiti sotto soglia più volte — considerare aumento ordine</div>
            {inventoryStats.alertProducts.length === 0
              ? <EmptyState icon={<Package size={32}/>} text="Nessun prodotto critico rilevato ✅"/>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {inventoryStats.alertProducts.slice(0, 8).map((prod, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff8f0', borderRadius: '10px', border: '1px solid #fed7aa' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.83rem', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prod.name}</div>
                        <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: '1px' }}>Giacenza: {prod.quantity} / soglia: {prod.reorderThreshold || 0}</div>
                      </div>
                      <span style={{ padding: '3px 10px', borderRadius: '999px', background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0, marginLeft: '10px' }}>{prod.lowStockAlerts || 0}x alert</span>
                    </div>
                  ))}
                </div>
            }
          </Card>
        </div>

        {/* Tutti i prodotti sotto soglia */}
        {inventoryStats.lowStockProducts.length > 0 && (
          <Card noPad>
            <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#0f172a' }}>📉 Tutti i prodotti sotto soglia di riordino</h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.855rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Prodotto', 'Categoria', 'Giacenza', 'Soglia', 'Deficit', 'Costo Unit.'].map(h => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: h === 'Prodotto' || h === 'Categoria' ? 'left' : 'right', fontWeight: '700', color: '#64748b', fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inventoryStats.lowStockProducts.map((prod, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fff8f0'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '12px 14px', fontWeight: '600', color: '#0f172a' }}>{prod.name}</td>
                      <td style={{ padding: '12px 14px', color: '#475569' }}>{prod.category || 'N/A'}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '700', color: '#dc2626' }}>{prod.quantity}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', color: '#475569' }}>{prod.reorderThreshold || 0}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '700', color: '#d97706' }}>{Math.max(0, (prod.reorderThreshold || 0) - prod.quantity)}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', color: '#475569' }}>€ {(prod.unitCost || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  //  MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════════

  // Loading state generico
  if (perfLoading && outerTab === 'performance') return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '12px', color: '#94a3b8' }}>
      <BarChart3 size={36} style={{ opacity: 0.3 }}/>
      <span style={{ fontSize: '0.9rem' }}>Caricamento dati in corso…</span>
    </div>
  );

  // Tab interni Performance
  const PERF_TABS = [
    { id: 'dashboard',     label: '📊 Dashboard'     },
    { id: 'medici',        label: '👨‍⚕️ Medici'        },
    { id: 'incassi',       label: '💰 Incassi'        },
    { id: 'assicurazioni', label: '🏥 Assicurazioni'  },
    { id: 'pazienti',      label: '🧑‍🤝‍🧑 Pazienti'      },
    { id: 'agenda',        label: '📅 Agenda'         },
    { id: 'avanzate',      label: '🚀 Avanzate'       },
  ];

  // Tab interni HR
  const HR_TABS = [
    { id: 'hr',        label: '👥 Personale & HR'     },
    { id: 'financials',label: '💰 Costi & Finanza'    },
    { id: 'rooms',     label: '🏥 Stanze & Operatività' },
    { id: 'inventory', label: '📦 Magazzino'           },
  ];

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>

      {/* ── OUTER HEADER ────────────────────────────────────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid #f1f5f9', padding: '0 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          {/* Title row */}
          <div style={{ padding: '1.5rem 0 0' }}>
            <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#0f172a', margin: '0 0 2px', letterSpacing: '-0.03em' }}>
              Reportistica & Analisi
            </h1>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>
              Centro medico · Dashboard analitica integrata
            </p>
          </div>

          {/* Outer tab switcher */}
          <div style={{ display: 'flex', gap: '2px', marginTop: '1.25rem' }}>
            {[
              { id: 'performance', label: '📊 Report Clienti & Performance' },
              { id: 'hr',          label: '👥 Report HR & Inventario'       },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setOuterTab(t.id)}
                style={{
                  padding: '12px 24px', border: 'none', cursor: 'pointer',
                  fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.15s',
                  background: 'transparent',
                  color: outerTab === t.id ? '#1d4ed8' : '#64748b',
                  borderBottom: outerTab === t.id ? '3px solid #1d4ed8' : '3px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTENT AREA ───────────────────────────────────────────────────── */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '2rem' }}>

        {/* ════ TAB: PERFORMANCE ════════════════════════════════════════════ */}
        {outerTab === 'performance' && (
          <>
            {/* Inner tab row + period selector */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '12px' }}>
              {/* Inner tabs */}
              <div style={{ display: 'flex', background: 'white', padding: '4px', borderRadius: '12px', gap: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflowX: 'auto' }}>
                {PERF_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setPerfTab(tab.id)} style={{ padding: '8px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', transition: 'all 0.15s', background: perfTab === tab.id ? '#1d4ed8' : 'transparent', color: perfTab === tab.id ? 'white' : '#64748b', whiteSpace: 'nowrap' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Period pills */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {periodBtns.map(p => (
                  <button key={p.v} onClick={() => setPeriod(p.v)} style={{ padding: '6px 14px', borderRadius: '999px', border: '1px solid', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600', transition: 'all 0.15s', borderColor: period === p.v ? '#1d4ed8' : '#e2e8f0', background: period === p.v ? '#dbeafe' : 'white', color: period === p.v ? '#1d4ed8' : '#64748b' }}>
                    {p.l}
                  </button>
                ))}
                {period === 'custom' && (
                  <>
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.78rem', background: 'white', color: '#0f172a' }}/>
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>→</span>
                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.78rem', background: 'white', color: '#0f172a' }}/>
                  </>
                )}
              </div>
            </div>

            {/* Performance content */}
            {perfTab === 'dashboard'     && renderDashboard()}
            {perfTab === 'medici'        && renderMedici()}
            {perfTab === 'incassi'       && renderIncassi()}
            {perfTab === 'assicurazioni' && renderAssicurazioni()}
            {perfTab === 'pazienti'      && renderPazienti()}
            {perfTab === 'agenda'        && renderAgenda()}
            {perfTab === 'avanzate' && renderAvanzate()} 
          </>
        )}

        {/* ════ TAB: HR & INVENTARIO ═══════════════════════════════════════ */}
        {outerTab === 'hr' && (
          <>
            {/* HR controls row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {/* HR inner tabs */}
              <div style={{ display: 'flex', background: 'white', padding: '4px', borderRadius: '12px', gap: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflowX: 'auto' }}>
                {HR_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setHrTab(tab.id)} style={{ padding: '8px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', transition: 'all 0.15s', background: hrTab === tab.id ? '#1d4ed8' : 'transparent', color: hrTab === tab.id ? 'white' : '#64748b', whiteSpace: 'nowrap' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Filtri periodo HR */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: 'white', padding: '10px 16px', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b' }}>Anno</span>
                  <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem', background: 'white', color: '#0f172a' }}>
                    {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b' }}>Da</span>
                  <select value={startMonth} onChange={e => setStartMonth(parseInt(e.target.value))} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem', background: 'white', color: '#0f172a' }}>
                    {moment.months().map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b' }}>A</span>
                  <select value={endMonth} onChange={e => setEndMonth(parseInt(e.target.value))} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem', background: 'white', color: '#0f172a' }}>
                    {moment.months().map((m, i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                </div>
                {(hrTab === 'hr' || hrTab === 'financials') && availableRoles.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b' }}>Ruolo</span>
                    <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem', background: 'white', color: '#0f172a' }}>
                      <option value="all">Tutti</option>
                      {availableRoles.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </div>
                )}
                <button onClick={fetchHRData} disabled={hrLoading} style={{ padding: '6px 16px', background: hrLoading ? '#f1f5f9' : '#1d4ed8', color: hrLoading ? '#94a3b8' : 'white', border: 'none', borderRadius: '8px', cursor: hrLoading ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: '600', transition: 'all 0.15s' }}>
                  {hrLoading ? 'Caricamento…' : '↻ Aggiorna'}
                </button>
              </div>
            </div>

            {/* HR loading overlay */}
            {hrLoading
              ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '40vh', gap: '12px', color: '#94a3b8' }}>
                  <BarChart3 size={36} style={{ opacity: 0.3 }}/>
                  <span style={{ fontSize: '0.9rem' }}>Caricamento dati HR…</span>
                </div>
              : <>
                  {hrTab === 'hr'         && renderHRPersonale()}
                  {hrTab === 'financials' && renderHRFinanza()}
                  {hrTab === 'rooms'      && renderHRStanze()}
                  {hrTab === 'inventory'  && renderHRInventario()}
                </>
            }
          </>
        )}
      </div>

      {/* Drill-down modal (HR) */}
      <DrillDownModal
        isOpen={drillDownModal.isOpen}
        onClose={() => setDrillDownModal(prev => ({ ...prev, isOpen: false }))}
        title={drillDownModal.title}
        data={drillDownModal.data}
        type={drillDownModal.type}
      />
    </div>
  );
} 